import { useState, useEffect, useRef, useCallback } from "react";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CONSTANTS & CONFIG
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const ARCHETYPE_CONFIG = {
  momentum_chaser: {
    label: "Momentum", icon: "M", color: "#1e40af", light: "#dbeafe",
    desc: "Follows the crowd, buys winners, sells losers",
    weight: 0.25,
    biasRanges: { herding: [0.70, 0.95], lossAversion: [1.5, 2.0], overconfidence: [0.30, 0.50], anchoring: [0.20, 0.40], recency: [0.60, 0.85], disposition: [0.50, 0.70] },
    riskDist: { aggressive: 0.6, moderate: 0.3, conservative: 0.1 },
    speedDist: { fast: 0.7, medium: 0.2, slow: 0.1 },
  },
  loss_averse: {
    label: "Loss Averse", icon: "L", color: "#991b1b", light: "#fee2e2",
    desc: "Holds losers too long, sells winners too early",
    weight: 0.20,
    biasRanges: { herding: [0.30, 0.50], lossAversion: [2.0, 3.0], overconfidence: [0.10, 0.30], anchoring: [0.50, 0.75], recency: [0.40, 0.60], disposition: [0.75, 0.95] },
    riskDist: { aggressive: 0.1, moderate: 0.4, conservative: 0.5 },
    speedDist: { fast: 0.1, medium: 0.3, slow: 0.6 },
  },
  overconfident: {
    label: "Overconfident", icon: "O", color: "#b45309", light: "#fef3c7",
    desc: "Overtrades, underestimates risk, concentrated positions",
    weight: 0.15,
    biasRanges: { herding: [0.20, 0.40], lossAversion: [1.0, 1.5], overconfidence: [0.75, 0.95], anchoring: [0.20, 0.35], recency: [0.50, 0.70], disposition: [0.30, 0.50] },
    riskDist: { aggressive: 0.8, moderate: 0.15, conservative: 0.05 },
    speedDist: { fast: 0.8, medium: 0.15, slow: 0.05 },
  },
  anchored: {
    label: "Anchored", icon: "A", color: "#7c3aed", light: "#ede9fe",
    desc: "Fixates on reference prices, seeks confirming data",
    weight: 0.15,
    biasRanges: { herding: [0.20, 0.40], lossAversion: [1.5, 2.25], overconfidence: [0.40, 0.60], anchoring: [0.75, 0.95], recency: [0.30, 0.50], disposition: [0.40, 0.60] },
    riskDist: { aggressive: 0.2, moderate: 0.5, conservative: 0.3 },
    speedDist: { fast: 0.2, medium: 0.5, slow: 0.3 },
  },
  panic_seller: {
    label: "Panic Seller", icon: "P", color: "#dc2626", light: "#fef2f2",
    desc: "Overweights dramatic events, flight-to-safety",
    weight: 0.10,
    biasRanges: { herding: [0.60, 0.80], lossAversion: [2.5, 3.5], overconfidence: [0.05, 0.20], anchoring: [0.30, 0.50], recency: [0.85, 0.98], disposition: [0.20, 0.40] },
    riskDist: { aggressive: 0.05, moderate: 0.25, conservative: 0.70 },
    speedDist: { fast: 0.9, medium: 0.08, slow: 0.02 },
  },
  contrarian: {
    label: "Contrarian", icon: "C", color: "#0e7490", light: "#ccfbf1",
    desc: "Fades crowd sentiment, buys fear, sells greed",
    weight: 0.10,
    biasRanges: { herding: [-0.70, -0.40], lossAversion: [1.2, 1.8], overconfidence: [0.50, 0.70], anchoring: [0.30, 0.50], recency: [0.20, 0.40], disposition: [0.20, 0.40] },
    riskDist: { aggressive: 0.5, moderate: 0.35, conservative: 0.15 },
    speedDist: { fast: 0.3, medium: 0.5, slow: 0.2 },
  },
  rational: {
    label: "Rational", icon: "R", color: "#166534", light: "#dcfce7",
    desc: "DCF-driven, rebalances systematically, benchmark agent",
    weight: 0.05,
    biasRanges: { herding: [0.0, 0.10], lossAversion: [1.0, 1.2], overconfidence: [0.05, 0.15], anchoring: [0.05, 0.15], recency: [0.10, 0.25], disposition: [0.05, 0.15] },
    riskDist: { aggressive: 0.3, moderate: 0.5, conservative: 0.2 },
    speedDist: { fast: 0.2, medium: 0.6, slow: 0.2 },
  },
};

const STOCKS_DATA = {
  // ══════════════════════════════════════════════════════════════════════════
  // US LISTED STOCKS (60+ major companies)
  // ══════════════════════════════════════════════════════════════════════════
  
  // ── Technology ──
  AAPL:  { name: "Apple Inc.",              sector: "Technology",              basePrice: 228, market: "US" },
  MSFT:  { name: "Microsoft Corp.",         sector: "Technology",              basePrice: 442, market: "US" },
  NVDA:  { name: "NVIDIA Corp.",            sector: "Technology",              basePrice: 138, market: "US" },
  AMD:   { name: "Adv. Micro Devices",      sector: "Technology",              basePrice: 118, market: "US" },
  INTC:  { name: "Intel Corp.",             sector: "Technology",              basePrice: 22,  market: "US" },
  CRM:   { name: "Salesforce Inc.",         sector: "Technology",              basePrice: 315, market: "US" },
  ORCL:  { name: "Oracle Corp.",            sector: "Technology",              basePrice: 178, market: "US" },
  IBM:   { name: "IBM Corp.",               sector: "Technology",              basePrice: 232, market: "US" },
  CSCO:  { name: "Cisco Systems",           sector: "Technology",              basePrice: 58,  market: "US" },
  ADBE:  { name: "Adobe Inc.",              sector: "Technology",              basePrice: 485, market: "US" },
  AVGO:  { name: "Broadcom Inc.",           sector: "Technology",              basePrice: 185, market: "US" },
  QCOM:  { name: "Qualcomm Inc.",           sector: "Technology",              basePrice: 165, market: "US" },
  TXN:   { name: "Texas Instruments",       sector: "Technology",              basePrice: 195, market: "US" },
  MU:    { name: "Micron Technology",       sector: "Technology",              basePrice: 88,  market: "US" },
  NOW:   { name: "ServiceNow Inc.",         sector: "Technology",              basePrice: 925, market: "US" },
  SNOW:  { name: "Snowflake Inc.",          sector: "Technology",              basePrice: 165, market: "US" },
  PLTR:  { name: "Palantir Technologies",   sector: "Technology",              basePrice: 78,  market: "US" },
  
  // ── Communication Services ──
  GOOGL: { name: "Alphabet Inc.",           sector: "Communication Services",  basePrice: 178, market: "US" },
  META:  { name: "Meta Platforms",          sector: "Communication Services",  basePrice: 612, market: "US" },
  DIS:   { name: "Walt Disney",             sector: "Communication Services",  basePrice: 112, market: "US" },
  NFLX:  { name: "Netflix Inc.",            sector: "Communication Services",  basePrice: 875, market: "US" },
  CMCSA: { name: "Comcast Corp.",           sector: "Communication Services",  basePrice: 38,  market: "US" },
  VZ:    { name: "Verizon Comm.",           sector: "Communication Services",  basePrice: 42,  market: "US" },
  TMUS:  { name: "T-Mobile US",             sector: "Communication Services",  basePrice: 245, market: "US" },
  
  // ── Consumer Discretionary ──
  AMZN:  { name: "Amazon.com Inc.",         sector: "Consumer Discretionary",  basePrice: 205, market: "US" },
  TSLA:  { name: "Tesla Inc.",              sector: "Consumer Discretionary",  basePrice: 272, market: "US" },
  HD:    { name: "Home Depot",              sector: "Consumer Discretionary",  basePrice: 388, market: "US" },
  MCD:   { name: "McDonald's Corp.",        sector: "Consumer Discretionary",  basePrice: 298, market: "US" },
  NKE:   { name: "Nike Inc.",               sector: "Consumer Discretionary",  basePrice: 72,  market: "US" },
  SBUX:  { name: "Starbucks Corp.",         sector: "Consumer Discretionary",  basePrice: 98,  market: "US" },
  LOW:   { name: "Lowe's Companies",        sector: "Consumer Discretionary",  basePrice: 258, market: "US" },
  TJX:   { name: "TJX Companies",           sector: "Consumer Discretionary",  basePrice: 125, market: "US" },
  BKNG:  { name: "Booking Holdings",        sector: "Consumer Discretionary",  basePrice: 4850, market: "US" },
  GM:    { name: "General Motors",          sector: "Consumer Discretionary",  basePrice: 48,  market: "US" },
  F:     { name: "Ford Motor Co.",          sector: "Consumer Discretionary",  basePrice: 11,  market: "US" },
  
  // ── Financials ──
  JPM:   { name: "JPMorgan Chase",          sector: "Financials",              basePrice: 248, market: "US" },
  BAC:   { name: "Bank of America",         sector: "Financials",              basePrice: 44,  market: "US" },
  GS:    { name: "Goldman Sachs",           sector: "Financials",              basePrice: 592, market: "US" },
  "BRK.B":{ name: "Berkshire Hathaway",     sector: "Financials",              basePrice: 478, market: "US" },
  V:     { name: "Visa Inc.",               sector: "Financials",              basePrice: 338, market: "US" },
  MA:    { name: "Mastercard Inc.",         sector: "Financials",              basePrice: 528, market: "US" },
  WFC:   { name: "Wells Fargo",             sector: "Financials",              basePrice: 72,  market: "US" },
  C:     { name: "Citigroup Inc.",          sector: "Financials",              basePrice: 68,  market: "US" },
  MS:    { name: "Morgan Stanley",          sector: "Financials",              basePrice: 118, market: "US" },
  AXP:   { name: "American Express",        sector: "Financials",              basePrice: 285, market: "US" },
  SCHW:  { name: "Charles Schwab",          sector: "Financials",              basePrice: 82,  market: "US" },
  BLK:   { name: "BlackRock Inc.",          sector: "Financials",              basePrice: 985, market: "US" },
  SPGI:  { name: "S&P Global Inc.",         sector: "Financials",              basePrice: 515, market: "US" },
  
  // ── Healthcare ──
  UNH:   { name: "UnitedHealth",            sector: "Healthcare",              basePrice: 502, market: "US" },
  JNJ:   { name: "Johnson & Johnson",       sector: "Healthcare",              basePrice: 152, market: "US" },
  PFE:   { name: "Pfizer Inc.",             sector: "Healthcare",              basePrice: 25,  market: "US" },
  ABBV:  { name: "AbbVie Inc.",             sector: "Healthcare",              basePrice: 178, market: "US" },
  MRK:   { name: "Merck & Co.",             sector: "Healthcare",              basePrice: 98,  market: "US" },
  LLY:   { name: "Eli Lilly & Co.",         sector: "Healthcare",              basePrice: 785, market: "US" },
  TMO:   { name: "Thermo Fisher",           sector: "Healthcare",              basePrice: 545, market: "US" },
  DHR:   { name: "Danaher Corp.",           sector: "Healthcare",              basePrice: 245, market: "US" },
  BMY:   { name: "Bristol-Myers Squibb",    sector: "Healthcare",              basePrice: 42,  market: "US" },
  GILD:  { name: "Gilead Sciences",         sector: "Healthcare",              basePrice: 98,  market: "US" },
  AMGN:  { name: "Amgen Inc.",              sector: "Healthcare",              basePrice: 295, market: "US" },
  MDT:   { name: "Medtronic plc",           sector: "Healthcare",              basePrice: 88,  market: "US" },
  CVS:   { name: "CVS Health",              sector: "Healthcare",              basePrice: 58,  market: "US" },
  
  // ── Energy ──
  XOM:   { name: "ExxonMobil",              sector: "Energy",                  basePrice: 108, market: "US" },
  CVX:   { name: "Chevron Corp.",           sector: "Energy",                  basePrice: 155, market: "US" },
  COP:   { name: "ConocoPhillips",          sector: "Energy",                  basePrice: 102, market: "US" },
  SLB:   { name: "Schlumberger Ltd.",       sector: "Energy",                  basePrice: 42,  market: "US" },
  EOG:   { name: "EOG Resources",           sector: "Energy",                  basePrice: 125, market: "US" },
  PXD:   { name: "Pioneer Natural Res.",    sector: "Energy",                  basePrice: 235, market: "US" },
  OXY:   { name: "Occidental Petroleum",    sector: "Energy",                  basePrice: 52,  market: "US" },
  
  // ── Industrials ──
  CAT:   { name: "Caterpillar Inc.",        sector: "Industrials",             basePrice: 385, market: "US" },
  GE:    { name: "GE Aerospace",            sector: "Industrials",             basePrice: 195, market: "US" },
  RTX:   { name: "RTX Corporation",         sector: "Industrials",             basePrice: 125, market: "US" },
  HON:   { name: "Honeywell Intl.",         sector: "Industrials",             basePrice: 208, market: "US" },
  UNP:   { name: "Union Pacific",           sector: "Industrials",             basePrice: 238, market: "US" },
  LMT:   { name: "Lockheed Martin",         sector: "Industrials",             basePrice: 465, market: "US" },
  BA:    { name: "Boeing Co.",              sector: "Industrials",             basePrice: 178, market: "US" },
  DE:    { name: "Deere & Company",         sector: "Industrials",             basePrice: 425, market: "US" },
  UPS:   { name: "United Parcel Service",   sector: "Industrials",             basePrice: 128, market: "US" },
  FDX:   { name: "FedEx Corp.",             sector: "Industrials",             basePrice: 275, market: "US" },
  
  // ── Consumer Staples ──
  PG:    { name: "Procter & Gamble",        sector: "Consumer Staples",        basePrice: 168, market: "US" },
  KO:    { name: "Coca-Cola Co.",           sector: "Consumer Staples",        basePrice: 62,  market: "US" },
  PEP:   { name: "PepsiCo Inc.",            sector: "Consumer Staples",        basePrice: 145, market: "US" },
  COST:  { name: "Costco Wholesale",        sector: "Consumer Staples",        basePrice: 985, market: "US" },
  WMT:   { name: "Walmart Inc.",            sector: "Consumer Staples",        basePrice: 92,  market: "US" },
  PM:    { name: "Philip Morris Intl.",     sector: "Consumer Staples",        basePrice: 132, market: "US" },
  MO:    { name: "Altria Group",            sector: "Consumer Staples",        basePrice: 52,  market: "US" },
  CL:    { name: "Colgate-Palmolive",       sector: "Consumer Staples",        basePrice: 92,  market: "US" },
  
  // ── Materials ──
  LIN:   { name: "Linde plc",               sector: "Materials",               basePrice: 458, market: "US" },
  APD:   { name: "Air Products",            sector: "Materials",               basePrice: 285, market: "US" },
  SHW:   { name: "Sherwin-Williams",        sector: "Materials",               basePrice: 345, market: "US" },
  FCX:   { name: "Freeport-McMoRan",        sector: "Materials",               basePrice: 42,  market: "US" },
  NEM:   { name: "Newmont Corp.",           sector: "Materials",               basePrice: 48,  market: "US" },
  
  // ── Real Estate ──
  AMT:   { name: "American Tower",          sector: "Real Estate",             basePrice: 198, market: "US" },
  PLD:   { name: "Prologis Inc.",           sector: "Real Estate",             basePrice: 112, market: "US" },
  CCI:   { name: "Crown Castle",            sector: "Real Estate",             basePrice: 98,  market: "US" },
  EQIX:  { name: "Equinix Inc.",            sector: "Real Estate",             basePrice: 925, market: "US" },
  
  // ── Utilities ──
  NEE:   { name: "NextEra Energy",          sector: "Utilities",               basePrice: 72,  market: "US" },
  DUK:   { name: "Duke Energy",             sector: "Utilities",               basePrice: 108, market: "US" },
  SO:    { name: "Southern Company",        sector: "Utilities",               basePrice: 88,  market: "US" },
  D:     { name: "Dominion Energy",         sector: "Utilities",               basePrice: 52,  market: "US" },

  // ══════════════════════════════════════════════════════════════════════════
  // CANADIAN TSX LISTED STOCKS (40+ major companies)
  // ══════════════════════════════════════════════════════════════════════════
  
  // ── Financials (Big 6 Banks + Insurers) ──
  "RY.TO":   { name: "Royal Bank of Canada",    sector: "Financials",             basePrice: 174, market: "TSX" },
  "TD.TO":   { name: "Toronto-Dominion Bank",   sector: "Financials",             basePrice: 78,  market: "TSX" },
  "BNS.TO":  { name: "Bank of Nova Scotia",     sector: "Financials",             basePrice: 68,  market: "TSX" },
  "BMO.TO":  { name: "Bank of Montreal",        sector: "Financials",             basePrice: 118, market: "TSX" },
  "CM.TO":   { name: "CIBC",                    sector: "Financials",             basePrice: 92,  market: "TSX" },
  "NA.TO":   { name: "National Bank",           sector: "Financials",             basePrice: 128, market: "TSX" },
  "MFC.TO":  { name: "Manulife Financial",      sector: "Financials",             basePrice: 42,  market: "TSX" },
  "SLF.TO":  { name: "Sun Life Financial",      sector: "Financials",             basePrice: 78,  market: "TSX" },
  "IFC.TO":  { name: "Intact Financial",        sector: "Financials",             basePrice: 268, market: "TSX" },
  "POW.TO":  { name: "Power Corp.",             sector: "Financials",             basePrice: 44,  market: "TSX" },
  "GWO.TO":  { name: "Great-West Lifeco",       sector: "Financials",             basePrice: 48,  market: "TSX" },
  
  // ── Energy ──
  "ENB.TO":  { name: "Enbridge Inc.",           sector: "Energy",                 basePrice: 62,  market: "TSX" },
  "CNQ.TO":  { name: "Canadian Natural Res.",   sector: "Energy",                 basePrice: 42,  market: "TSX" },
  "SU.TO":   { name: "Suncor Energy",           sector: "Energy",                 basePrice: 52,  market: "TSX" },
  "TRP.TO":  { name: "TC Energy Corp.",         sector: "Energy",                 basePrice: 58,  market: "TSX" },
  "CVE.TO":  { name: "Cenovus Energy",          sector: "Energy",                 basePrice: 22,  market: "TSX" },
  "IMO.TO":  { name: "Imperial Oil",            sector: "Energy",                 basePrice: 98,  market: "TSX" },
  "PPL.TO":  { name: "Pembina Pipeline",        sector: "Energy",                 basePrice: 52,  market: "TSX" },
  "KEY.TO":  { name: "Keyera Corp.",            sector: "Energy",                 basePrice: 42,  market: "TSX" },
  "ARX.TO":  { name: "ARC Resources",           sector: "Energy",                 basePrice: 26,  market: "TSX" },
  "TOU.TO":  { name: "Tourmaline Oil",          sector: "Energy",                 basePrice: 62,  market: "TSX" },
  
  // ── Materials (Mining & Resources) ──
  "ABX.TO":  { name: "Barrick Gold",            sector: "Materials",              basePrice: 26,  market: "TSX" },
  "NTR.TO":  { name: "Nutrien Ltd.",            sector: "Materials",              basePrice: 62,  market: "TSX" },
  "FNV.TO":  { name: "Franco-Nevada",           sector: "Materials",              basePrice: 165, market: "TSX" },
  "AEM.TO":  { name: "Agnico Eagle Mines",      sector: "Materials",              basePrice: 88,  market: "TSX" },
  "FM.TO":   { name: "First Quantum Minerals",  sector: "Materials",              basePrice: 15,  market: "TSX" },
  "TECK.B.TO": { name: "Teck Resources",        sector: "Materials",              basePrice: 52,  market: "TSX" },
  "WPM.TO":  { name: "Wheaton Precious Metals", sector: "Materials",              basePrice: 72,  market: "TSX" },
  "K.TO":    { name: "Kinross Gold",            sector: "Materials",              basePrice: 12,  market: "TSX" },
  "CCO.TO":  { name: "Cameco Corp.",            sector: "Materials",              basePrice: 68,  market: "TSX" },
  
  // ── Industrials ──
  "CP.TO":   { name: "Canadian Pacific Kansas", sector: "Industrials",            basePrice: 104, market: "TSX" },
  "CNR.TO":  { name: "Canadian National Rail",  sector: "Industrials",            basePrice: 148, market: "TSX" },
  "WCN.TO":  { name: "Waste Connections",       sector: "Industrials",            basePrice: 225, market: "TSX" },
  "CAE.TO":  { name: "CAE Inc.",                sector: "Industrials",            basePrice: 32,  market: "TSX" },
  "TIH.TO":  { name: "Toromont Industries",     sector: "Industrials",            basePrice: 118, market: "TSX" },
  "STN.TO":  { name: "Stantec Inc.",            sector: "Industrials",            basePrice: 118, market: "TSX" },
  
  // ── Technology ──
  "SHOP.TO": { name: "Shopify Inc.",            sector: "Technology",             basePrice: 132, market: "TSX" },
  "CSU.TO":  { name: "Constellation Software",  sector: "Technology",             basePrice: 4250, market: "TSX" },
  "OTEX.TO": { name: "Open Text Corp.",         sector: "Technology",             basePrice: 42,  market: "TSX" },
  "BB.TO":   { name: "BlackBerry Ltd.",         sector: "Technology",             basePrice: 4,   market: "TSX" },
  "LSPD.TO": { name: "Lightspeed Commerce",     sector: "Technology",             basePrice: 18,  market: "TSX" },
  
  // ── Communication Services ──
  "BCE.TO":  { name: "BCE Inc.",                sector: "Communication Services", basePrice: 34,  market: "TSX" },
  "T.TO":    { name: "Telus Corp.",             sector: "Communication Services", basePrice: 22,  market: "TSX" },
  "RCI.B.TO": { name: "Rogers Communications", sector: "Communication Services", basePrice: 52,  market: "TSX" },
  "QBR.B.TO": { name: "Quebecor Inc.",          sector: "Communication Services", basePrice: 32,  market: "TSX" },
  
  // ── Consumer Staples ──
  "ATD.TO":  { name: "Alimentation Couche-Tard", sector: "Consumer Staples",      basePrice: 78,  market: "TSX" },
  "L.TO":    { name: "Loblaw Companies",        sector: "Consumer Staples",       basePrice: 185, market: "TSX" },
  "MRU.TO":  { name: "Metro Inc.",              sector: "Consumer Staples",       basePrice: 88,  market: "TSX" },
  "EMP.A.TO": { name: "Empire Company",         sector: "Consumer Staples",       basePrice: 42,  market: "TSX" },
  "SAP.TO":  { name: "Saputo Inc.",             sector: "Consumer Staples",       basePrice: 28,  market: "TSX" },
  
  // ── Consumer Discretionary ──
  "DOL.TO":  { name: "Dollarama Inc.",          sector: "Consumer Discretionary", basePrice: 145, market: "TSX" },
  "QSR.TO":  { name: "Restaurant Brands Intl.", sector: "Consumer Discretionary", basePrice: 92,  market: "TSX" },
  "MG.TO":   { name: "Magna International",     sector: "Consumer Discretionary", basePrice: 52,  market: "TSX" },
  "LNR.TO":  { name: "Linamar Corp.",           sector: "Consumer Discretionary", basePrice: 58,  market: "TSX" },
  
  // ── Utilities ──
  "FTS.TO":  { name: "Fortis Inc.",             sector: "Utilities",              basePrice: 58,  market: "TSX" },
  "H.TO":    { name: "Hydro One Ltd.",          sector: "Utilities",              basePrice: 45,  market: "TSX" },
  "EMA.TO":  { name: "Emera Inc.",              sector: "Utilities",              basePrice: 52,  market: "TSX" },
  "AQN.TO":  { name: "Algonquin Power",         sector: "Utilities",              basePrice: 8,   market: "TSX" },
  "CU.TO":   { name: "Canadian Utilities",      sector: "Utilities",              basePrice: 32,  market: "TSX" },
  
  // ── Real Estate ──
  "REI.UN.TO": { name: "RioCan REIT",           sector: "Real Estate",            basePrice: 18,  market: "TSX" },
  "CAR.UN.TO": { name: "Canadian Apartment REIT", sector: "Real Estate",          basePrice: 42,  market: "TSX" },
  "HR.UN.TO": { name: "H&R REIT",               sector: "Real Estate",            basePrice: 10,  market: "TSX" },
  "AP.UN.TO": { name: "Allied Properties REIT", sector: "Real Estate",            basePrice: 18,  market: "TSX" },
  "GRT.UN.TO": { name: "Granite REIT",          sector: "Real Estate",            basePrice: 72,  market: "TSX" },
  
  // ── Healthcare ──
  "WSP.TO":  { name: "WSP Global Inc.",         sector: "Industrials",            basePrice: 245, market: "TSX" },
};

const STOCK_SYMBOLS = Object.keys(STOCKS_DATA);

const HEADLINES = [
  { title: "Federal Reserve raises interest rates by 50 basis points in surprise move", source: "Reuters", cat: "Central Bank", sentiment: -0.35, sectors: ["Financials", "Technology"] },
  { title: "Apple reports record Q4 earnings beating Wall Street estimates by 12%", source: "Bloomberg", cat: "Earnings", sentiment: 0.40, sectors: ["Technology", "Consumer Discretionary"] },
  { title: "NVIDIA announces breakthrough AI chip with 3x performance at half the cost", source: "WSJ", cat: "Technology", sentiment: 0.50, sectors: ["Technology"] },
  { title: "Oil prices surge 8% as OPEC announces emergency production cuts", source: "CNBC", cat: "Commodities", sentiment: 0.15, sectors: ["Energy"] },
  { title: "US-China trade tensions escalate with sweeping semiconductor export ban", source: "Financial Times", cat: "Geopolitical", sentiment: -0.45, sectors: ["Technology", "Consumer Discretionary"] },
  { title: "Major US bank discloses unexpected $4.2B derivatives trading loss", source: "Reuters", cat: "Financials", sentiment: -0.55, sectors: ["Financials"] },
  { title: "Bitcoin surges past $120,000 driven by massive institutional ETF inflows", source: "CoinDesk", cat: "Crypto", sentiment: 0.35, sectors: ["Financials", "Technology"] },
  { title: "US unemployment claims fall to 52-year low signaling robust labor market", source: "Associated Press", cat: "Macro", sentiment: 0.25, sectors: ["Consumer Discretionary", "Financials"] },
  { title: "European Central Bank signals aggressive rate cut cycle beginning Q3", source: "Bloomberg", cat: "Central Bank", sentiment: 0.20, sectors: ["Financials"] },
  { title: "Pfizer receives FDA fast-track approval for next-generation cancer therapy", source: "STAT News", cat: "Healthcare", sentiment: 0.45, sectors: ["Healthcare"] },
];

const AGENT_NAMES = [
  "Sarah Chen", "James Park", "Maria Santos", "David Kim", "Emma Wilson",
  "Michael Okafor", "Lisa Zhang", "Robert Taylor", "Priya Sharma", "Thomas Mueller",
  "Akiko Tanaka", "Carlos Rivera", "Sophie Laurent", "Ahmed Hassan", "Elena Volkov",
  "Daniel Nkomo", "Rachel Cooper", "Hiroshi Yamamoto", "Isabella Rossi", "Nathan Brooks",
  "Fatima Al-Rashid", "Owen McCarthy", "Yuki Sato", "Marcus Johnson", "Aisha Patel",
  "Lars Eriksson", "Camila Ferreira", "Dmitri Kozlov", "Grace Okonkwo", "Hugo Morales",
  "Mei Lin Wu", "Patrick O'Brien", "Zara Ndongo", "Viktor Petrov", "Ana Gutierrez",
  "Kenji Watanabe", "Nadia Kowalski", "Samuel Adeyemi", "Clara Hoffmann", "Raj Mehta",
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// UTILITY FUNCTIONS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const pick = (arr) => arr.length ? arr[Math.floor(Math.random() * arr.length)] : undefined;
const pickWeighted = (obj) => {
  const r = Math.random();
  let cum = 0;
  for (const [k, w] of Object.entries(obj)) { cum += w; if (r <= cum) return k; }
  return Object.keys(obj).pop();
};
const hexRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? `${parseInt(m[1],16)},${parseInt(m[2],16)},${parseInt(m[3],16)}` : "0,0,0";
};
const fmt$ = (n) => {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${sign}$${(abs/1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs/1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// BIAS ENGINE (Behavioral Finance Math)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const BiasEngine = {
  prospectValue(outcome, lambda, alpha = 0.88, beta = 0.88) {
    return outcome >= 0 ? Math.pow(outcome, alpha) : -lambda * Math.pow(-outcome, beta);
  },
  herdingAdjust(herdStrength, marketConsensus) {
    return herdStrength * Math.tanh(3.0 * marketConsensus);
  },
  overconfidenceFilter(signal, risk, oScore) {
    return { signal: signal * (1 + oScore), risk: risk * (1 - oScore * 0.5) };
  },
  anchoringAdjust(anchor, newInfo, weight) {
    return anchor + (1 - weight) * (newInfo - anchor);
  },
  recencyWeight(history, bias) {
    if (!history.length) return 0;
    let wSum = 0, tSum = 0;
    for (let i = 0; i < history.length; i++) {
      const w = Math.pow(bias, i);
      wSum += history[history.length - 1 - i] * w;
      tSum += w;
    }
    return wSum / tSum;
  },
  dispositionEffect(pnl, strength) {
    if (pnl > 0) return { bias: "sell", pressure: strength * Math.min(pnl / 10, 1) };
    return { bias: "hold", pressure: strength * Math.min(Math.abs(pnl) / 10, 1) };
  },
  cascadeCheck(sentiments, threshold = 0.65) {
    if (!sentiments.length) return { active: false, direction: null, pct: 0 };
    const bull = sentiments.filter(s => s > 0.15).length / sentiments.length;
    const bear = sentiments.filter(s => s < -0.15).length / sentiments.length;
    if (bull > threshold) return { active: true, direction: "bullish", pct: bull };
    if (bear > threshold) return { active: true, direction: "bearish", pct: bear };
    return { active: false, direction: null, pct: Math.max(bull, bear) };
  },
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AGENT THOUGHT ENGINE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const THOUGHT_TEMPLATES = {
  momentum_chaser: {
    BUY: [
      "Everyone's buying {stock}, I need to get in before I miss the move",
      "The trend on {stock} is clearly up, riding the wave",
      "{stock} momentum is strong, the crowd knows something",
      "Can't afford to be left behind on {stock}, adding now",
    ],
    SELL: [
      "The herd is selling {stock}, I'm getting out too",
      "{stock} is falling and nobody's catching it, dumping now",
      "Following the smart money out of {stock}",
      "Momentum has flipped on {stock}, time to exit",
    ],
  },
  loss_averse: {
    BUY: [
      "Adding a small position in {stock}, keeping it conservative",
      "{stock} seems safe enough to cautiously dip in",
      "Slowly building {stock}, can't risk too much",
    ],
    SELL: [
      "Can't watch {stock} losses grow any further, cutting now",
      "The pain on {stock} is unbearable, stopping the bleeding",
      "Every dollar lost on {stock} feels like two, I'm out",
      "Selling {stock} before it gets worse, protecting capital",
    ],
  },
  overconfident: {
    BUY: [
      "I've analyzed {stock} deeply and the market is dead wrong, loading up",
      "Everyone's panicking about {stock} but I see the real picture",
      "My analysis says {stock} is massively undervalued, going big",
      "{stock} is a no-brainer at these levels, aggressive entry",
    ],
    SELL: [
      "Called the {stock} top perfectly, taking profits",
      "My model predicted {stock} would drop, rotating out",
      "I knew {stock} was overvalued, exiting at just the right time",
    ],
  },
  anchored: {
    BUY: [
      "{stock} is well below its fair value anchor, buying the dip",
      "At this price {stock} is cheap versus historical range",
      "The market has overreacted on {stock}, it'll revert to fair value",
    ],
    SELL: [
      "{stock} has reached my price target, taking profits",
      "{stock} is above fair value based on fundamentals, trimming",
      "My target for {stock} was hit, disciplined exit",
    ],
  },
  panic_seller: {
    BUY: [
      "Things seem to be calming down, cautiously adding {stock}",
      "Maybe the worst is over for {stock}, tiny position",
    ],
    SELL: [
      "This is terrifying, getting out of {stock} immediately",
      "Everything is crashing, dumping {stock} before it hits zero",
      "I can't handle this risk anymore, liquidating {stock}",
      "This feels like 2008 all over again, selling {stock} now",
    ],
  },
  contrarian: {
    BUY: [
      "Maximum fear on {stock} means maximum opportunity, buying",
      "When everyone's selling {stock}, that's my buy signal",
      "Blood in the streets on {stock}, time to be greedy",
      "The crowd is wrong about {stock}, going against consensus",
    ],
    SELL: [
      "Too much euphoria around {stock}, taking the other side",
      "When everyone loves {stock}, it's time to sell",
      "{stock} greed levels are unsustainable, fading this rally",
    ],
  },
  rational: {
    BUY: [
      "Rebalancing into {stock} per my systematic allocation model",
      "{stock} fits the risk-adjusted return criteria, adding per plan",
      "Quarterly rebalance signals underweight on {stock}, adjusting",
    ],
    SELL: [
      "Trimming {stock} to maintain target allocation ratios",
      "{stock} now overweight in portfolio, systematic reduction",
      "Risk-adjusted metrics on {stock} have deteriorated, reducing",
    ],
  },
};

function generateThought(archetype, action, stock) {
  if (action === "HOLD") return "Holding steady, no compelling reason to act";
  const templates = THOUGHT_TEMPLATES[archetype]?.[action];
  if (!templates || !templates.length) return action + " " + stock + " based on current conditions";
  return templates[Math.floor(Math.random() * templates.length)].replace(/\{stock\}/g, stock);
}

const THEME_PATTERNS = [
  { id: "herd_fear", label: "Herd-driven fear selling", keywords: ["everyone", "herd", "crowd", "following", "left behind"], color: "#991b1b", bg: "#fee2e2" },
  { id: "panic_exit", label: "Panic liquidation", keywords: ["terrifying", "crashing", "zero", "can't handle", "2008"], color: "#dc2626", bg: "#fef2f2" },
  { id: "loss_pain", label: "Loss aversion and pain of losses", keywords: ["pain", "unbearable", "bleeding", "dollar lost", "worse", "protecting"], color: "#b91c1c", bg: "#fee2e2" },
  { id: "contrarian_buy", label: "Contrarian buying into fear", keywords: ["fear", "opportunity", "blood", "wrong about", "greedy", "against consensus"], color: "#166534", bg: "#dcfce7" },
  { id: "overconfidence", label: "Overconfidence in own analysis", keywords: ["analyzed", "market is wrong", "no-brainer", "called", "I knew", "perfectly", "dead wrong"], color: "#b45309", bg: "#fef3c7" },
  { id: "anchor_revert", label: "Anchoring to fair value / targets", keywords: ["fair value", "anchor", "historical", "overreacted", "revert", "target"], color: "#7c3aed", bg: "#ede9fe" },
  { id: "momentum_ride", label: "Riding the momentum / trend", keywords: ["trend", "wave", "momentum", "move", "flipped", "smart money"], color: "#1e40af", bg: "#dbeafe" },
  { id: "systematic", label: "Systematic disciplined rebalancing", keywords: ["rebalancing", "systematic", "allocation", "risk-adjusted", "criteria", "model"], color: "#166534", bg: "#dcfce7" },
];

function extractThemes(agents) {
  const results = THEME_PATTERNS.map(t => ({ ...t, count: 0, quotes: [] }));
  agents.forEach(a => {
    (a.decisionHistory || []).forEach(d => {
      if (!d.reasoning) return;
      const lower = d.reasoning.toLowerCase();
      results.forEach(theme => {
        if (theme.keywords.some(kw => lower.includes(kw))) {
          theme.count++;
          if (theme.quotes.length < 3) {
            theme.quotes.push({ name: d.agentName, archetype: d.archetype, text: d.reasoning, action: d.action, stock: d.stock });
          }
        }
      });
    });
  });
  return results.filter(t => t.count > 0).sort((a, b) => b.count - a.count);
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AGENT GENERATION
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function createAgentPopulation(count, headlineData) {
  const agents = [];
  const types = Object.entries(ARCHETYPE_CONFIG);
  let nameIdx = 0;

  types.forEach(([key, cfg]) => {
    const n = Math.round(cfg.weight * count);
    for (let i = 0; i < n && agents.length < count; i++) {
      const biases = {};
      Object.entries(cfg.biasRanges).forEach(([b, [lo, hi]]) => { biases[b] = rand(lo, hi); });

      const riskTol = pickWeighted(cfg.riskDist);
      const speed = pickWeighted(cfg.speedDist);
      const sectorAffinity = headlineData.sectors.length
        ? pick(headlineData.sectors)
        : pick(["Technology", "Financials", "Healthcare"]);

      const sectorStocks = STOCK_SYMBOLS.filter(s => STOCKS_DATA[s].sector === sectorAffinity);
      const otherStocks = STOCK_SYMBOLS.filter(s => STOCKS_DATA[s].sector !== sectorAffinity);
      const holdings = [
        ...sectorStocks.sort(() => Math.random() - 0.5).slice(0, 2),
        ...otherStocks.sort(() => Math.random() - 0.5).slice(0, rand(1, 3) | 0),
      ];

      const experience = (1 + Math.random() * 25) | 0;

      agents.push({
        id: `a${String(agents.length).padStart(3, "0")}`,
        name: AGENT_NAMES[nameIdx % AGENT_NAMES.length],
        archetype: key,
        biases,
        riskTolerance: riskTol,
        reactionSpeed: speed,
        experience,
        influence: clamp(rand(0.1, 0.9) * (experience / 25), 0.05, 1),
        holdings,
        sectorFocus: sectorAffinity,
        cash: rand(riskTol === "conservative" ? 80000 : 10000, riskTol === "aggressive" ? 200000 : 500000),
        portfolioValue: rand(50000, 2500000),
        sentiment: headlineData.sentiment * rand(0.2, 0.8) * (key === "contrarian" ? -0.7 : 1) + rand(-0.15, 0.15),
        pnl: 0,
        anchorPrice: 100,
        decisionHistory: [],
        sentimentHistory: [],
        currentAction: null,
        actionTimer: Math.floor(rand(3, speed === "fast" ? 12 : speed === "medium" ? 20 : 30)),
        // Physics
        x: rand(60, 740), y: rand(60, 440),
        vx: rand(-0.3, 0.3), vy: rand(-0.3, 0.3),
        trail: [],
        pulse: Math.random() * Math.PI * 2,
        radius: 5 + rand(0, 1) * 8,
      });
      nameIdx++;
    }
  });

  // Fill remaining
  while (agents.length < count) {
    const base = agents[Math.floor(Math.random() * agents.length)];
    agents.push({
      ...JSON.parse(JSON.stringify(base)),
      id: `a${String(agents.length).padStart(3, "0")}`,
      name: AGENT_NAMES[nameIdx % AGENT_NAMES.length],
      x: rand(60, 740), y: rand(60, 440),
      sentiment: base.sentiment + rand(-0.1, 0.1),
    });
    nameIdx++;
  }

  return agents;
}

function createStockState() {
  const state = {};
  STOCK_SYMBOLS.forEach(sym => {
    const base = STOCKS_DATA[sym].basePrice;
    state[sym] = {
      price: base + rand(-base * 0.03, base * 0.03),
      open: base,
      change: 0,
      changePct: 0,
      volume: 0,
      buyPressure: 0,
      sellPressure: 0,
      history: [base],
      sector: STOCKS_DATA[sym].sector,
      name: STOCKS_DATA[sym].name,
      market: STOCKS_DATA[sym].market,
    };
  });
  return state;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SIMULATION ENGINE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function simulationStep(agents, stocks, headlineData, step) {
  const newOrders = [];

  // Reset per-step stock accumulators
  Object.values(stocks).forEach(s => { s.volume = 0; s.buyPressure = 0; s.sellPressure = 0; });

  // Market consensus
  const totalInfluence = agents.reduce((s, a) => s + a.influence, 0) || 1;
  const mktSentiment = agents.reduce((s, a) => s + a.sentiment * a.influence, 0) / totalInfluence;

  // Cascade check
  const cascade = BiasEngine.cascadeCheck(agents.map(a => a.sentiment));

  agents.forEach(agent => {
    const cfg = ARCHETYPE_CONFIG[agent.archetype];

    // ── 1. Sentiment dynamics ──
    const herdAdj = BiasEngine.herdingAdjust(agent.biases.herding, mktSentiment);
    const recencyMem = BiasEngine.recencyWeight(agent.sentimentHistory.slice(-10), agent.biases.recency);

    agent.sentiment += herdAdj * 0.08;
    agent.sentiment += recencyMem * 0.02;
    agent.sentiment += rand(-0.025, 0.025); // noise

    // Cascade amplification
    if (cascade.active) {
      const cascadeDir = cascade.direction === "bullish" ? 1 : -1;
      if (agent.archetype !== "contrarian" && agent.archetype !== "rational") {
        agent.sentiment += cascadeDir * agent.biases.herding * 0.03;
      } else if (agent.archetype === "contrarian") {
        agent.sentiment -= cascadeDir * Math.abs(agent.biases.herding) * 0.04;
      }
    }

    agent.sentiment = clamp(agent.sentiment, -1, 1);
    agent.sentimentHistory.push(agent.sentiment);
    if (agent.sentimentHistory.length > 50) agent.sentimentHistory.shift();

    // ── 2. Swarm physics ──
    const targetX = 400 + agent.sentiment * 280;
    const targetY = 250 - agent.sentiment * 160;

    let herdFx = 0, herdFy = 0, herdN = 0;
    agents.forEach(other => {
      if (other.id === agent.id) return;
      const dx = other.x - agent.x;
      const dy = other.y - agent.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Archetype herding
      if (dist < 130 && other.archetype === agent.archetype) {
        const str = agent.archetype === "contrarian" ? -0.0015 : 0.002;
        herdFx += dx * str;
        herdFy += dy * str;
        herdN++;
      }
      // Separation
      if (dist < 28 && dist > 0) {
        agent.vx -= (dx / dist) * 0.4;
        agent.vy -= (dy / dist) * 0.4;
      }
    });

    if (herdN > 0) { agent.vx += herdFx / herdN; agent.vy += herdFy / herdN; }

    // Sentiment target pull
    agent.vx += (targetX - agent.x) * 0.004;
    agent.vy += (targetY - agent.y) * 0.004;

    // Damping + noise
    agent.vx *= 0.94;
    agent.vy *= 0.94;
    agent.vx += rand(-0.25, 0.25);
    agent.vy += rand(-0.25, 0.25);

    agent.x = clamp(agent.x + agent.vx, 16, 784);
    agent.y = clamp(agent.y + agent.vy, 16, 484);

    agent.trail.push({ x: agent.x, y: agent.y });
    if (agent.trail.length > 24) agent.trail.shift();
    agent.pulse += 0.06;

    // ── 3. Trading decision ──
    agent.actionTimer--;
    if (agent.actionTimer <= 0) {
      const baseSignal = agent.sentiment + headlineData.sentiment * 0.15;
      const { signal: percSignal, risk: percRisk } = BiasEngine.overconfidenceFilter(
        baseSignal, 0.5, agent.biases.overconfidence
      );

      const anchorAdj = BiasEngine.anchoringAdjust(agent.anchorPrice, 100 + baseSignal * 20, agent.biases.anchoring);
      const dispEffect = BiasEngine.dispositionEffect(agent.pnl, agent.biases.disposition);

      let decisionScore = percSignal * 0.5 + (anchorAdj - 100) * 0.01;

      // Disposition bias modification
      if (dispEffect.bias === "sell" && decisionScore > 0) decisionScore -= dispEffect.pressure * 0.3;
      if (dispEffect.bias === "hold" && decisionScore < 0) decisionScore += dispEffect.pressure * 0.3;

      // Prospect theory: asymmetric reaction
      const prospectMod = BiasEngine.prospectValue(decisionScore, agent.biases.lossAversion);
      decisionScore = decisionScore * 0.6 + prospectMod * 0.4;

      let action, magnitude;
      if (decisionScore > 0.12) { action = "BUY"; magnitude = Math.min(decisionScore, 1); }
      else if (decisionScore < -0.12) { action = "SELL"; magnitude = Math.min(Math.abs(decisionScore), 1); }
      else { action = "HOLD"; magnitude = 0; }

      const stock = agent.holdings.length > 0 ? pick(agent.holdings) : null;
      const shares = action !== "HOLD" ? Math.floor(magnitude * rand(30, 600) * (agent.riskTolerance === "aggressive" ? 1.8 : agent.riskTolerance === "conservative" ? 0.4 : 1)) : 0;

      if (action !== "HOLD" && stock && stocks[stock]) {
        const reasoning = generateThought(agent.archetype, action, stock);
        const order = {
          id: `o_${step}_${agent.id}_${Math.random().toString(36).slice(2,6)}`,
          agentId: agent.id, agentName: agent.name, archetype: agent.archetype,
          action, stock, shares,
          price: stocks[stock].price,
          magnitude, sentiment: agent.sentiment, step,
          reasoning,
        };
        newOrders.push(order);
        agent.currentAction = order;
        agent.decisionHistory.push(order);
        if (agent.decisionHistory.length > 30) agent.decisionHistory.shift();

        if (action === "BUY") {
          stocks[stock].buyPressure += shares * agent.influence;
          stocks[stock].volume += shares;
          agent.pnl -= shares * stocks[stock].price * 0.001; // simplified
        } else {
          stocks[stock].sellPressure += shares * agent.influence;
          stocks[stock].volume += shares;
          agent.pnl += shares * stocks[stock].price * 0.001;
        }
      } else {
        agent.currentAction = { action: "HOLD", stock: agent.holdings[0] || "---" };
      }

      const baseInterval = agent.reactionSpeed === "fast" ? 8 : agent.reactionSpeed === "medium" ? 16 : 28;
      agent.actionTimer = Math.floor(baseInterval + rand(-3, 5));
    }
  });

  // ── 4. Stock price updates ──
  Object.entries(stocks).forEach(([sym, s]) => {
    const net = (s.buyPressure - s.sellPressure) / Math.max(s.buyPressure + s.sellPressure, 1);
    const volatility = 0.002 + Math.abs(mktSentiment) * 0.003;
    const priceMove = net * s.price * volatility + rand(-0.3, 0.3);
    s.price = Math.max(1, s.price + priceMove);
    s.change = s.price - s.open;
    s.changePct = ((s.price - s.open) / s.open) * 100;
    s.history.push(s.price);
    if (s.history.length > 80) s.history.shift();
  });

  return { mktSentiment, cascade, newOrders };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CANVAS RENDERER
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function renderSwarm(canvas, agents, cascade, hoveredId, selectedId) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.offsetWidth;
  const h = canvas.offsetHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.scale(dpr, dpr);

  const sx = w / 800;
  const sy = h / 500;

  // Background
  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(0, 0, w, h);

  // Grid
  ctx.strokeStyle = "rgba(148,163,184,0.1)";
  ctx.lineWidth = 0.5;
  for (let gx = 0; gx < w; gx += 50) { ctx.beginPath(); ctx.moveTo(gx, 0); ctx.lineTo(gx, h); ctx.stroke(); }
  for (let gy = 0; gy < h; gy += 50) { ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke(); }

  // Sentiment gradient zones
  const zoneGrad = ctx.createLinearGradient(0, 0, w, 0);
  zoneGrad.addColorStop(0, "rgba(153,27,27,0.05)");
  zoneGrad.addColorStop(0.35, "rgba(153,27,27,0.02)");
  zoneGrad.addColorStop(0.5, "rgba(148,163,184,0.01)");
  zoneGrad.addColorStop(0.65, "rgba(22,101,52,0.02)");
  zoneGrad.addColorStop(1, "rgba(22,101,52,0.05)");
  ctx.fillStyle = zoneGrad;
  ctx.fillRect(0, 0, w, h);

  // Zone labels
  ctx.font = `600 ${10 * sx}px Georgia, serif`;
  ctx.fillStyle = "rgba(153,27,27,0.25)";
  ctx.textAlign = "left";
  ctx.fillText("FEAR", 12, 20 * sy);
  ctx.fillStyle = "rgba(22,101,52,0.25)";
  ctx.textAlign = "right";
  ctx.fillText("GREED", w - 12, 20 * sy);
  ctx.fillStyle = "rgba(100,116,139,0.2)";
  ctx.textAlign = "center";
  ctx.fillText("NEUTRAL", w / 2, 20 * sy);

  // Center divider
  ctx.strokeStyle = "rgba(148,163,184,0.12)";
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 6]);
  ctx.beginPath(); ctx.moveTo(w / 2, 30 * sy); ctx.lineTo(w / 2, h - 10); ctx.stroke();
  ctx.setLineDash([]);

  // ── Connection lines ──
  for (let i = 0; i < agents.length; i++) {
    for (let j = i + 1; j < agents.length; j++) {
      if (agents[i].archetype !== agents[j].archetype) continue;
      const dx = (agents[i].x - agents[j].x) * sx;
      const dy = (agents[i].y - agents[j].y) * sy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 70) {
        const alpha = (1 - dist / 70) * 0.1;
        ctx.strokeStyle = `rgba(${hexRgb(ARCHETYPE_CONFIG[agents[i].archetype].color)},${alpha})`;
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(agents[i].x * sx, agents[i].y * sy);
        ctx.lineTo(agents[j].x * sx, agents[j].y * sy);
        ctx.stroke();
      }
    }
  }

  // ── Trails ──
  agents.forEach(a => {
    if (a.trail.length < 2) return;
    const col = hexRgb(ARCHETYPE_CONFIG[a.archetype].color);
    for (let i = 1; i < a.trail.length; i++) {
      const alpha = (i / a.trail.length) * 0.15;
      ctx.strokeStyle = `rgba(${col},${alpha})`;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.moveTo(a.trail[i - 1].x * sx, a.trail[i - 1].y * sy);
      ctx.lineTo(a.trail[i].x * sx, a.trail[i].y * sy);
      ctx.stroke();
    }
  });

  // ── Agents ──
  agents.forEach(a => {
    const cfg = ARCHETYPE_CONFIG[a.archetype];
    const ax = a.x * sx, ay = a.y * sy;
    const pulse = 1 + Math.sin(a.pulse) * 0.12;
    const r = a.radius * pulse * Math.min(sx, sy) * 0.85;
    const isHov = hoveredId === a.id;
    const isSel = selectedId === a.id;
    const col = hexRgb(cfg.color);

    // Action glow
    if (a.currentAction && a.currentAction.action === "BUY" && a.actionTimer > a.actionTimer * 0.5) {
      ctx.beginPath(); ctx.arc(ax, ay, r + 7, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(22,101,52,0.15)"; ctx.fill();
    } else if (a.currentAction && a.currentAction.action === "SELL" && a.actionTimer > a.actionTimer * 0.5) {
      ctx.beginPath(); ctx.arc(ax, ay, r + 7, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(153,27,27,0.15)"; ctx.fill();
    }

    // Selection/hover ring
    if (isSel) {
      ctx.beginPath(); ctx.arc(ax, ay, r + 5, 0, Math.PI * 2);
      ctx.strokeStyle = cfg.color; ctx.lineWidth = 2.5; ctx.stroke();
    } else if (isHov) {
      ctx.beginPath(); ctx.arc(ax, ay, r + 4, 0, Math.PI * 2);
      ctx.strokeStyle = cfg.color; ctx.lineWidth = 1.5; ctx.stroke();
    }

    // Body
    const sentAlpha = 0.45 + Math.abs(a.sentiment) * 0.55;
    ctx.beginPath(); ctx.arc(ax, ay, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${col},${sentAlpha})`;
    ctx.fill();
    ctx.strokeStyle = `rgba(${col},0.7)`;
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Icon
    ctx.font = `700 ${Math.max(7, r * 0.85)}px system-ui,sans-serif`;
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(cfg.icon, ax, ay + 0.5);

    // Trade label
    if (a.currentAction && a.currentAction.action !== "HOLD" && a.actionTimer > 2) {
      const isB = a.currentAction.action === "BUY";
      ctx.font = `700 ${8 * Math.min(sx, sy)}px system-ui,sans-serif`;
      ctx.fillStyle = isB ? "rgba(22,101,52,0.85)" : "rgba(153,27,27,0.85)";
      ctx.textBaseline = "bottom";
      ctx.fillText(`${isB ? "+" : "-"}${a.currentAction.stock}`, ax, ay - r - 4);
    }
  });

  // Cascade border
  if (cascade && cascade.active) {
    ctx.save();
    ctx.strokeStyle = cascade.direction === "bullish" ? "rgba(22,101,52,0.35)" : "rgba(220,38,38,0.35)";
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 5]);
    ctx.strokeRect(3, 3, w - 6, h - 6);
    ctx.restore();
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MAIN COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export default function MiroFishSwarm() {
  const [phase, setPhase] = useState("input");
  const [headline, setHeadline] = useState("");
  const [headlineData, setHeadlineData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [stocks, setStocks] = useState({});
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [mktSentiment, setMktSentiment] = useState(0);
  const [sentHistory, setSentHistory] = useState([]);
  const [orderFlow, setOrderFlow] = useState([]);
  const [cascade, setCascade] = useState({ active: false });
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [hoveredAgent, setHoveredAgent] = useState(null);
  const [totalOrders, setTotalOrders] = useState({ buys: 0, sells: 0 });
  const [tab, setTab] = useState("flow"); // flow | stocks | archetypes

  // Report & Agent Chat
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatAgentId, setChatAgentId] = useState(null);

  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const dataRef = useRef({ agents: [], stocks: {}, headlineData: null, step: 0, cascade: { active: false } });
  const frameCount = useRef(0);
  const hoveredRef = useRef(null);
  const selectedRef = useRef(null);

  // ── Live Headlines ──
  const [liveHeadlines, setLiveHeadlines] = useState([]);
  const [headlinesLoading, setHeadlinesLoading] = useState(false);
  const [headlinesError, setHeadlinesError] = useState(null);
  const [headlinesTimestamp, setHeadlinesTimestamp] = useState(null);
  const fetchIdRef = useRef(0);

  const fetchLiveHeadlines = useCallback(async () => {
    const thisId = ++fetchIdRef.current;
    setHeadlinesLoading(true);
    setHeadlinesError(null);
    setLiveHeadlines([]);

    try {
      const response = await fetch("/api/headlines", { method: "POST" });
      if (thisId !== fetchIdRef.current) return;

      if (!response.ok) {
        throw new Error("Server returned " + response.status);
      }

      const data = await response.json();
      if (thisId !== fetchIdRef.current) return;

      if (data.headlines && data.headlines.length >= 3) {
        setLiveHeadlines(data.headlines);
        setHeadlinesTimestamp(new Date().toLocaleTimeString());
        setHeadlinesLoading(false);
        return;
      }
      throw new Error("No headlines returned");
    } catch (err) {
      if (thisId !== fetchIdRef.current) return;
      setHeadlinesError(err.message || "Failed to fetch");
      setHeadlinesLoading(false);
    }
  }, []);

  // Auto-fetch once on first mount
  const didAutoFetch = useRef(false);
  useEffect(() => {
    if (phase === "input" && !didAutoFetch.current) {
      didAutoFetch.current = true;
      fetchLiveHeadlines();
    }
  }, [phase, fetchLiveHeadlines]);

  // Only fall back to static headlines if NOT currently loading
  const displayHeadlines = liveHeadlines.length > 0 ? liveHeadlines : (headlinesLoading ? [] : HEADLINES);
  const isShowingLive = liveHeadlines.length > 0;

  // ── ReportAgent: Client-Side Analysis (no API needed) ──
  const generateReport = useCallback(() => {
    setReportLoading(true);
    setReport(null);

    try {
    // Build all the data
    const arcSum = {};
    Object.keys(ARCHETYPE_CONFIG).forEach(k => { arcSum[k] = { count: 0, avgSent: 0, buys: 0, sells: 0, topStocks: {} }; });
    agents.forEach(a => {
      const s = arcSum[a.archetype];
      if (!s) return;
      s.count++; s.avgSent += a.sentiment;
      (a.decisionHistory || []).forEach(d => {
        if (d.action === "BUY") { s.buys++; s.topStocks[d.stock] = (s.topStocks[d.stock] || 0) + d.shares; }
        if (d.action === "SELL") { s.sells++; }
      });
    });
    Object.values(arcSum).forEach(s => { if (s.count) s.avgSent /= s.count; });

    const topMovers = Object.entries(stocks).sort((a, b) => Math.abs(b[1].changePct) - Math.abs(a[1].changePct)).slice(0, 8);
    const themes = extractThemes(agents);
    const spotlights = agents.filter(a => (a.decisionHistory || []).length > 2).sort((a, b) => Math.abs(b.sentiment) - Math.abs(a.sentiment)).slice(0, 4);
    const sentimentLabel = mktSentiment > 0.2 ? "strongly bullish" : mktSentiment > 0.05 ? "mildly bullish" : mktSentiment < -0.2 ? "strongly bearish" : mktSentiment < -0.05 ? "mildly bearish" : "neutral";
    const dominantBias = themes[0]?.label || "mixed behavioral patterns";
    const cascadeNote = cascade.active ? `A ${cascade.direction} herding cascade was detected with ${(cascade.pct * 100).toFixed(0)}% of agents aligned.` : "No herding cascade was detected during this simulation.";

    const topBought = topMovers.filter(([,s]) => s.changePct > 0).slice(0, 3);
    const topSold = topMovers.filter(([,s]) => s.changePct < 0).slice(0, 3);

    const lines = [];
    lines.push("EXECUTIVE SUMMARY");
    lines.push(`In response to the headline "${headline}", the 40-agent swarm produced a ${sentimentLabel} market reaction over ${step} simulation steps. The dominant behavioral pattern was ${dominantBias.toLowerCase()}, resulting in ${totalOrders.buys} buy orders and ${totalOrders.sells} sell orders. ${cascadeNote}`);
    lines.push("");

    lines.push("SENTIMENT DYNAMICS");
    lines.push(`Aggregate market sentiment settled at ${mktSentiment.toFixed(3)}. ${mktSentiment < -0.1 ? "Fear dominated the swarm, with most agents clustering in the bearish zone." : mktSentiment > 0.1 ? "Greed dominated, with agents clustering on the bullish side." : "The market remained split between bulls and bears."}`);
    const biggestArch = Object.entries(arcSum).sort((a, b) => Math.abs(b[1].avgSent) - Math.abs(a[1].avgSent))[0];
    lines.push(`The most extreme archetype was ${ARCHETYPE_CONFIG[biggestArch[0]]?.label} (avg sentiment: ${biggestArch[1].avgSent.toFixed(3)}). ${cascade.active ? "The cascade amplified existing sentiment through positive feedback loops." : "Contrarian agents provided some stabilizing counterforce."}`);
    lines.push("");

    lines.push("BEHAVIORAL BIAS ATTRIBUTION");
    themes.slice(0, 4).forEach(t => {
      lines.push(`${t.label}: ${t.count} instances detected. ${t.quotes[0] ? `Example: "${t.quotes[0].text}" (${t.quotes[0].name}, ${ARCHETYPE_CONFIG[t.quotes[0].archetype]?.label})` : ""}`);
    });
    lines.push("");

    lines.push("AGENT SPOTLIGHT INTERVIEWS");
    spotlights.forEach(a => {
      const cfg = ARCHETYPE_CONFIG[a.archetype];
      const lastTrade = (a.decisionHistory || []).slice(-1)[0];
      const tradeCount = (a.decisionHistory || []).length;
      lines.push(`${a.name} (${cfg?.label}, sentiment: ${a.sentiment.toFixed(3)}):`);
      lines.push(`Made ${tradeCount} trades during the simulation. ${lastTrade ? `Most recent: ${lastTrade.action} ${lastTrade.stock} (${lastTrade.shares} shares). Reasoning: "${lastTrade.reasoning || "N/A"}"` : "No recent trades."}`);
      lines.push(`As a ${cfg?.label.toLowerCase()}, ${a.name}'s behavior was driven by ${cfg?.desc?.split(",")[0]?.toLowerCase() || "their archetype's core bias"}.`);
      lines.push("");
    });

    lines.push("CASCADE & CONTAGION ANALYSIS");
    lines.push(cascadeNote);
    if (cascade.active) {
      const triggerType = cascade.direction === "bullish" ? "momentum_chaser" : "panic_seller";
      lines.push(`The cascade was most likely triggered by ${ARCHETYPE_CONFIG[triggerType]?.label} agents, whose high herding coefficients (0.7-0.95) amplified the initial sentiment signal. ${ARCHETYPE_CONFIG.contrarian.label} agents (${arcSum.contrarian?.count || 0} in population) acted as partial counterweights but were outnumbered.`);
    } else {
      lines.push("The 65% alignment threshold was not breached. The presence of contrarian and rational agents prevented sentiment from cascading into a full herding event.");
    }
    lines.push("");

    lines.push("MARKET IMPACT");
    if (topBought.length) lines.push(`Top gainers: ${topBought.map(([s, d]) => `${s} (+${d.changePct.toFixed(2)}%)`).join(", ")}`);
    if (topSold.length) lines.push(`Top decliners: ${topSold.map(([s, d]) => `${s} (${d.changePct.toFixed(2)}%)`).join(", ")}`);
    lines.push(`The buy/sell ratio of ${(totalOrders.buys / (totalOrders.sells || 1)).toFixed(2)} suggests ${totalOrders.buys > totalOrders.sells ? "net accumulation" : "net distribution"} across the simulated market.`);
    lines.push("");

    lines.push("IMPLICATIONS FOR REAL MARKETS");
    lines.push(`This simulation demonstrates how ${dominantBias.toLowerCase()} can ${cascade.active ? "amplify into cascading market moves that exceed fundamental justification" : "influence market dynamics even without triggering full cascade events"}. The ${(arcSum.rational?.count || 0)} rational agents produced measurably different outcomes from the ${agents.length - (arcSum.rational?.count || 0)} biased agents, illustrating the Kahneman-Tversky prospect theory prediction that loss aversion and herding dominate in stress scenarios.`);

    // Small delay for visual feedback
    setTimeout(() => {
      setReport(lines.join("\n"));
      setReportLoading(false);
    }, 400);
    } catch (err) {
      console.error("Report generation error:", err);
      setReport("Error generating report: " + (err.message || "Unknown error. Try pausing after more simulation steps."));
      setReportLoading(false);
    }
  }, [agents, stocks, headline, step, mktSentiment, cascade, totalOrders]);

  // ── Agent Chat: Client-Side (no API needed) ──
  const CHAT_RESPONSES = {
    momentum_chaser: {
      sell: [
        "Look, when everyone's heading for the exits, I'm not going to be the last one holding the bag. I saw the momentum flip on {stock} and got out.",
        "The trend broke down on {stock}. I follow the market's lead -- when it tells me to sell, I sell. Simple as that.",
        "I watch what the smart money does. They were dumping {stock}, so I followed. That's how I've survived in this market."
      ],
      buy: [
        "The crowd is piling into {stock} for good reason. When momentum is this strong, you ride the wave or get left behind.",
        "I saw the buying pressure on {stock} building and jumped in. When the herd moves, I move with it.",
        "Everyone's talking about {stock}. The momentum is undeniable. I'm not going to miss this move."
      ],
      general: [
        "I've learned to trust the crowd. Markets are efficient enough that when everyone's moving one direction, there's usually a reason.",
        "My strategy is simple -- follow the momentum. When the trend is your friend, you don't fight it.",
        "This headline shifted market sentiment fast. I positioned accordingly because you can't afford to be contrarian when the whole market is moving."
      ],
    },
    loss_averse: {
      sell: [
        "I couldn't take the pain anymore on {stock}. Every tick lower felt like getting punched in the gut. I had to stop the bleeding.",
        "The losses on {stock} were keeping me up at night. I know I held too long, but cutting a loss is the hardest thing in investing.",
        "I finally sold {stock}. The fear of losing more outweighed everything else. I'd rather lock in the loss than watch it get worse."
      ],
      buy: [
        "I'm being very careful with {stock}. Small position, tight stop. I can't afford another big loss right now.",
        "I added a little {stock} but I'm nervous about it. My risk tolerance is pretty low after recent market volatility."
      ],
      general: [
        "After this headline, all I can think about is protecting my downside. Gains are nice, but avoiding losses is what matters.",
        "I tend to hold losing positions longer than I should. I know it's a problem, but selling at a loss just feels wrong.",
        "The emotional weight of losses is so much heavier than the joy of gains. That shapes every decision I make."
      ],
    },
    overconfident: {
      sell: [
        "I saw this coming on {stock}. My analysis predicted this downturn weeks ago. Sold right at the top.",
        "While everyone else was panicking, I had already positioned my {stock} exit. That's the advantage of deep analysis.",
        "I knew {stock} was overvalued. The market is finally catching up to what I already figured out."
      ],
      buy: [
        "The market has {stock} completely wrong. My proprietary analysis shows it's massively undervalued here. Going big.",
        "I've done more research on {stock} than 99% of traders. At this price, it's basically free money.",
        "Everyone's scared of {stock} but they're not seeing what I'm seeing. My conviction is extremely high here."
      ],
      general: [
        "I trust my own analysis over the consensus. The crowd is usually wrong, and I've got a strong track record.",
        "After this headline, most people will overreact. That creates opportunities for someone like me who can see through the noise.",
        "My returns speak for themselves. I'm confident in my positions regardless of what the headline says."
      ],
    },
    anchored: {
      sell: [
        "{stock} hit my price target so I trimmed. It doesn't matter what the headline says -- I have my levels and I stick to them.",
        "Based on historical ranges, {stock} was above fair value. Discipline means selling when your target is reached.",
        "I sold {stock} because it exceeded the fundamental value I calculated months ago. Prices always revert to the mean."
      ],
      buy: [
        "{stock} dropped well below my fair value estimate. That's a buying opportunity based on the fundamentals.",
        "My analysis has {stock} anchored at a much higher value. At this discount, the risk-reward is favorable.",
        "The market is overreacting to this headline. {stock}'s intrinsic value hasn't changed. I'm buying the dip."
      ],
      general: [
        "I don't let headlines move me much. I have my models, my price targets, and I stick to them.",
        "Fair value is fair value. Short-term sentiment doesn't change what a company is worth.",
        "I've been watching these price levels for months. My anchors tell me where the real value is."
      ],
    },
    panic_seller: {
      sell: [
        "I had to get out of {stock}. This feels like a real crisis and I can't afford to lose everything.",
        "I dumped {stock} immediately. Maybe I'm overreacting, but I'd rather be safe than sorry.",
        "This reminds me of 2008. I'm not waiting around to find out. Sold {stock} and moving to cash."
      ],
      buy: [
        "I bought a tiny bit of {stock} but my hands are shaking. Things seem to be calming down... maybe.",
        "I'm cautiously putting a little back into {stock}. But if it drops again, I'm out instantly."
      ],
      general: [
        "This headline terrifies me. I'm in capital preservation mode. Cash is king right now.",
        "I know I react emotionally, but when you've seen how fast money can disappear, you don't take chances.",
        "Every headline like this makes me want to sell everything. The risk just isn't worth it."
      ],
    },
    contrarian: {
      sell: [
        "Everyone loves {stock} right now. That's exactly when I sell. Peak euphoria is my exit signal.",
        "When the crowd is this greedy on {stock}, I take the other side. The consensus is almost always wrong at extremes.",
        "I sold {stock} because sentiment was way too positive. Mean reversion is inevitable."
      ],
      buy: [
        "Blood in the streets on {stock}? That's my buy signal. Maximum fear equals maximum opportunity.",
        "Everyone's dumping {stock} in a panic. History shows that's exactly when you should be accumulating.",
        "I bought {stock} because the crowd is dead wrong. When sentiment is this negative, the bottom is usually in."
      ],
      general: [
        "My whole strategy is doing the opposite of what feels natural. When everyone's scared, I get greedy.",
        "This headline has everyone running for the exits. That tells me it's time to start looking for bargains.",
        "The beauty of being a contrarian is that your best opportunities come when others are in maximum pain."
      ],
    },
    rational: {
      sell: [
        "I trimmed {stock} as part of my quarterly rebalance. The position was overweight relative to my target allocation.",
        "My systematic model flagged {stock} for reduction based on risk-adjusted metrics. Nothing emotional about it.",
        "I reduced {stock} to maintain portfolio diversification. The headline doesn't change my process."
      ],
      buy: [
        "Added {stock} to bring my allocation back to target weights. Pure portfolio rebalancing.",
        "My model identified {stock} as underweight relative to optimal allocation. Systematic purchase.",
        "I bought {stock} based on quantitative risk-return criteria. The headline creates noise, not signal."
      ],
      general: [
        "I follow a systematic process. Headlines create emotional reactions in others, which creates opportunities for disciplined investors.",
        "My portfolio is built on mathematical optimization, not gut feelings. I rebalance when the numbers tell me to.",
        "Behavioral biases are the biggest drag on investor returns. My edge is not having them."
      ],
    },
  };

  const chatWithAgent = useCallback((question) => {
    if (!selectedAgent || !question.trim()) return;
    const agent = dataRef.current.agents.find(a => a.id === selectedAgent.id) || selectedAgent;
    const cfg = ARCHETYPE_CONFIG[agent.archetype];
    const q = question.toLowerCase();

    setChatMessages(prev => [...prev, { role: "user", text: question }]);
    setChatInput("");
    setChatLoading(true);

    try {
    // Determine response category
    const lastTrade = (agent.decisionHistory || []).slice(-1)[0];
    const responses = CHAT_RESPONSES[agent.archetype] || CHAT_RESPONSES.rational;
    let pool;

    if (q.includes("sell") || q.includes("sold") || q.includes("dump") || q.includes("exit")) {
      pool = responses.sell || responses.general;
    } else if (q.includes("buy") || q.includes("bought") || q.includes("add") || q.includes("enter")) {
      pool = responses.buy || responses.general;
    } else if (lastTrade && lastTrade.action === "SELL") {
      pool = responses.sell || responses.general;
    } else if (lastTrade && lastTrade.action === "BUY") {
      pool = responses.buy || responses.general;
    } else {
      pool = responses.general || responses.sell || responses.buy;
    }

    if (!pool || !pool.length) {
      pool = ["I'm watching the market carefully and making decisions based on my analysis of the current situation."];
    }

    const stock = lastTrade?.stock || agent.holdings?.[0] || "the market";
    let reply = pool[Math.floor(Math.random() * pool.length)].replace(/\{stock\}/g, stock);

    // Add trade context if they ask about specifics
    if (q.includes("why") && lastTrade) {
      reply += ` My last trade was ${lastTrade.action} ${lastTrade.shares} shares of ${lastTrade.stock}. ${lastTrade.reasoning || ""}`;
    }

    setTimeout(() => {
      setChatMessages(prev => [...prev, { role: "agent", text: reply }]);
      setChatLoading(false);
    }, 300 + Math.random() * 400);
    } catch (err) {
      console.error("Chat error:", err);
      setTimeout(() => {
        setChatMessages(prev => [...prev, { role: "agent", text: "I'm having trouble organizing my thoughts right now. Let me think about that differently..." }]);
        setChatLoading(false);
      }, 200);
    }
  }, [selectedAgent]);

  // Reset chat when selecting a different agent
  useEffect(() => {
    if (selectedAgent && selectedAgent.id !== chatAgentId) {
      setChatMessages([]);
      setChatAgentId(selectedAgent.id);
    }
  }, [selectedAgent, chatAgentId]);

  // ── Launch ──
  const launch = useCallback((hl) => {
    const hlData = displayHeadlines.find(h => h.title === hl) || {
      title: hl, sentiment: hl.match(/surge|record|break|buy|strong|low|beat|jump|rally|gain/i) ? 0.3 : hl.match(/loss|raise|ban|tension|crash|decline|fall|drop|cut|warn|fear/i) ? -0.3 : 0,
      sectors: ["Technology", "Financials"], cat: "General", source: "User Input",
    };
    const ags = createAgentPopulation(40, hlData);
    const stk = createStockState();

    dataRef.current = { agents: ags, stocks: stk, headlineData: hlData, step: 0, cascade: { active: false } };
    setAgents([...ags]);
    setStocks({ ...stk });
    setHeadlineData(hlData);
    setStep(0);
    setMktSentiment(0);
    setSentHistory([]);
    setOrderFlow([]);
    setCascade({ active: false });
    setSelectedAgent(null);
    setTotalOrders({ buys: 0, sells: 0 });
    setReport(null); setReportLoading(false);
    setChatMessages([]); setChatAgentId(null); setChatInput("");
    setPhase("sim");
    setRunning(true);
  }, [displayHeadlines]);

  // ── Simulation loop ──
  useEffect(() => {
    if (phase !== "sim" || !running) return;
    let active = true;

    const loop = () => {
      if (!active) return;
      frameCount.current++;

      const d = dataRef.current;
      for (let s = 0; s < speed; s++) {
        d.step++;
        const result = simulationStep(d.agents, d.stocks, d.headlineData, d.step);

        // Store cascade in dataRef for canvas renderer access
        d.cascade = result.cascade;

        // Accumulate orders
        if (result.newOrders.length > 0) {
          const buys = result.newOrders.filter(o => o.action === "BUY").length;
          const sells = result.newOrders.filter(o => o.action === "SELL").length;
          setTotalOrders(prev => ({ buys: prev.buys + buys, sells: prev.sells + sells }));
          setOrderFlow(prev => [...result.newOrders, ...prev].slice(0, 50));
        }

        if (frameCount.current % 2 === 0) {
          setCascade(result.cascade);
          setMktSentiment(result.mktSentiment);
          setSentHistory(prev => [...prev.slice(-100), result.mktSentiment]);
        }
      }

      // Use refs for hover/selected so animation loop doesn't restart on every interaction
      renderSwarm(canvasRef.current, d.agents, d.cascade, hoveredRef.current, selectedRef.current);

      if (frameCount.current % 3 === 0) {
        setAgents([...d.agents]);
        setStocks({ ...d.stocks });
        setStep(d.step);

        // Keep selected agent detail panel in sync with live data
        if (selectedRef.current) {
          const liveAgent = d.agents.find(a => a.id === selectedRef.current);
          if (liveAgent) setSelectedAgent({ ...liveAgent });
        }
      }

      animRef.current = requestAnimationFrame(loop);
    };
    loop();
    return () => { active = false; if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [phase, running, speed]);

  // Re-render on selection change while paused
  useEffect(() => {
    if (phase === "sim" && !running) {
      renderSwarm(canvasRef.current, dataRef.current.agents, dataRef.current.cascade, hoveredRef.current, selectedRef.current);
    }
  }, [selectedAgent, hoveredAgent, phase, running]);

  // Sync refs so animation loop reads latest values without restarting
  useEffect(() => { hoveredRef.current = hoveredAgent; }, [hoveredAgent]);
  useEffect(() => { selectedRef.current = selectedAgent?.id || null; }, [selectedAgent]);

  // ── Canvas interaction ──
  const onCanvasClick = useCallback((e) => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const sx = c.offsetWidth / 800;
    const sy = c.offsetHeight / 500;

    let best = null, bestD = Infinity;
    dataRef.current.agents.forEach(a => {
      const dx = mx - a.x * sx;
      const dy = my - a.y * sy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < 22 && d < bestD) { best = a; bestD = d; }
    });
    setSelectedAgent(best ? { ...best } : null);
  }, []);

  const onCanvasMove = useCallback((e) => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const sx = c.offsetWidth / 800;
    const sy = c.offsetHeight / 500;

    let found = null;
    dataRef.current.agents.forEach(a => {
      const dx = mx - a.x * sx;
      const dy = my - a.y * sy;
      if (Math.sqrt(dx * dx + dy * dy) < 18) found = a.id;
    });
    setHoveredAgent(found);
  }, []);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDER: INPUT PHASE
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  if (phase === "input") {
    return (
      <div style={S.page}>
        <div style={{ maxWidth: 920, margin: "0 auto", padding: "48px 28px" }}>
          {/* Logo */}
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 12 }}>
              {Object.values(ARCHETYPE_CONFIG).map((c, i) => (
                <div key={i} style={{ width: 6 + i, height: 6 + i, borderRadius: "50%", background: c.color, opacity: 0.7, boxShadow: `0 0 ${4+i}px ${c.color}40` }} />
              ))}
            </div>
            <h1 style={{ ...S.heading, fontSize: 36, margin: "0 0 4px", letterSpacing: "-0.03em" }}>
              MiroFish Investor Swarm
            </h1>
            <p style={{ ...S.heading, fontSize: 14, color: "#64748b", margin: 0, fontWeight: 400, fontStyle: "italic" }}>
              Multi-agent behavioral finance simulation engine
            </p>
          </div>

          {/* Input box */}
          <div style={{ ...S.card, padding: "28px 32px" }}>
            <div style={{ ...S.label, marginBottom: 10 }}>Enter a financial news headline</div>
            <div style={{ display: "flex", gap: 12 }}>
              <input
                value={headline} onChange={e => setHeadline(e.target.value)}
                onKeyDown={e => e.key === "Enter" && headline.trim() && launch(headline.trim())}
                placeholder="e.g., Federal Reserve raises interest rates by 50 basis points"
                style={S.input}
                onFocus={e => e.target.style.borderColor = "#1e40af"}
                onBlur={e => e.target.style.borderColor = "#cbd5e1"}
              />
              <button onClick={() => headline.trim() && launch(headline.trim())} disabled={!headline.trim()}
                style={{ ...S.btnPrimary, opacity: headline.trim() ? 1 : 0.4, cursor: headline.trim() ? "pointer" : "default" }}>
                Launch Simulation
              </button>
            </div>
          </div>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "28px 0" }}>
            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
            <span style={{ ...S.heading, fontSize: 12, color: "#94a3b8", fontWeight: 400, fontStyle: "italic" }}>
              {headlinesLoading ? "fetching live headlines..." : liveHeadlines.length > 0 ? "today's live headlines" : "or select a headline below"}
            </span>
            <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
          </div>

          {/* Live headlines status bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {headlinesLoading ? (
                <>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", animation: "pulse 1s infinite" }} />
                  <span style={{ fontSize: 11, color: "#92400e", fontWeight: 600 }}>Searching live financial news via AI...</span>
                </>
              ) : isShowingLive ? (
                <>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#166534", boxShadow: "0 0 6px rgba(22,101,52,0.3)" }} />
                  <span style={{ fontSize: 11, color: "#166534", fontWeight: 600 }}>Live headlines loaded</span>
                  {headlinesTimestamp && <span style={{ fontSize: 10, color: "#94a3b8" }}>at {headlinesTimestamp}</span>}
                </>
              ) : headlinesError ? (
                <>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc2626" }} />
                  <span style={{ fontSize: 11, color: "#991b1b", fontWeight: 600 }}>Live fetch failed</span>
                  <span style={{ fontSize: 10, color: "#94a3b8" }}>Showing sample headlines. Try Refresh.</span>
                </>
              ) : (
                <>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#94a3b8" }} />
                  <span style={{ fontSize: 11, color: "#64748b" }}>Sample headlines</span>
                </>
              )}
            </div>
            <button
              onClick={() => { if (!headlinesLoading) fetchLiveHeadlines(); }}
              disabled={headlinesLoading}
              style={{
                padding: "5px 14px", fontSize: 11, fontFamily: "Georgia, serif", fontWeight: 600,
                background: headlinesLoading ? "#fef3c7" : "#dbeafe", color: headlinesLoading ? "#92400e" : "#1e40af",
                border: `1px solid ${headlinesLoading ? "#fde68a" : "#93c5fd"}`, borderRadius: 6,
                cursor: headlinesLoading ? "default" : "pointer", transition: "all 0.2s",
              }}
            >
              {headlinesLoading ? "Searching..." : "Refresh Live Headlines"}
            </button>
          </div>

          {/* Error detail banner */}
          {headlinesError && !headlinesLoading && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#dc2626", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600 }}>Could not fetch live headlines</div>
                <div style={{ fontSize: 10, color: "#b91c1c", marginTop: 2 }}>{headlinesError}</div>
              </div>
              <button onClick={() => fetchLiveHeadlines()} style={{ fontSize: 10, padding: "4px 12px", borderRadius: 4, border: "1px solid #fecaca", background: "#fff", color: "#991b1b", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                Retry
              </button>
            </div>
          )}

          {/* Loading skeleton */}
          {headlinesLoading && displayHeadlines.length === 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} style={{ padding: "14px 16px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, animation: "pulse 1.5s infinite", animationDelay: `${i * 0.1}s` }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 60, height: 14, borderRadius: 4, background: "#f1f5f9" }} />
                    <div style={{ width: 50, height: 14, borderRadius: 4, background: "#f1f5f9" }} />
                  </div>
                  <div style={{ width: "90%", height: 14, borderRadius: 4, background: "#f1f5f9", marginBottom: 4 }} />
                  <div style={{ width: "60%", height: 14, borderRadius: 4, background: "#f1f5f9" }} />
                </div>
              ))}
            </div>
          )}

          {/* Headlines Grid */}
          {displayHeadlines.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {displayHeadlines.map((hl, i) => (
                <button key={i} onClick={() => { setHeadline(hl.title); launch(hl.title); }}
                  style={S.headlineCard}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#1e40af"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 3px 12px rgba(30,64,175,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                    {hl.live && <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: "#dcfce7", color: "#166534", letterSpacing: "0.05em" }}>LIVE</span>}
                    <span style={{ ...S.badge, background: "#f1f5f9", color: "#475569" }}>{hl.cat}</span>
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>{hl.source}</span>
                    <span style={{ fontSize: 10, marginLeft: "auto", color: hl.sentiment > 0.15 ? "#166534" : hl.sentiment < -0.15 ? "#991b1b" : "#64748b", fontWeight: 600 }}>
                      {hl.sentiment > 0.15 ? "Bullish" : hl.sentiment < -0.15 ? "Bearish" : "Neutral"}
                    </span>
                  </div>
                  <div style={{ ...S.heading, fontSize: 13, color: "#0f172a", fontWeight: 400, lineHeight: 1.5 }}>{hl.title}</div>
                </button>
              ))}
            </div>
          )}

          {/* Footer */}
          <div style={{ textAlign: "center", marginTop: 40, fontSize: 11, color: "#94a3b8" }}>
            <span style={S.heading}>Built on </span>
            <a href="https://github.com/nikmcfly/MiroFish-Offline" target="_blank" rel="noopener" style={{ color: "#1e40af", textDecoration: "none", fontFamily: "Georgia, serif" }}>MiroFish-Offline</a>
            <span style={S.heading}> | Vitta Finance Academy | Above the Spin</span>
          </div>
        </div>
      </div>
    );
  }

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDER: SIMULATION
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const sentPct = ((mktSentiment + 1) / 2) * 100;
  const topStocks = Object.entries(stocks).filter(([,s]) => s.volume > 0).sort((a, b) => b[1].volume - a[1].volume).slice(0, 10);
  const archetypeStats = {};
  Object.keys(ARCHETYPE_CONFIG).forEach(k => { archetypeStats[k] = { count: 0, buy: 0, sell: 0, hold: 0, avgSent: 0 }; });
  agents.forEach(a => {
    const s = archetypeStats[a.archetype];
    s.count++;
    s.avgSent += a.sentiment;
    const act = a.currentAction?.action || "HOLD";
    s[act.toLowerCase()] = (s[act.toLowerCase()] || 0) + 1;
  });
  Object.values(archetypeStats).forEach(s => { if (s.count) s.avgSent /= s.count; });

  // ── Pause Summary: aggregate all orders by stock ──
  const pauseSummary = (() => {
    if (running || orderFlow.length === 0) return null;
    const stockAgg = {};
    // Scan ALL agent decision histories for complete picture
    agents.forEach(a => {
      (a.decisionHistory || []).forEach(d => {
        if (!d.stock || d.action === "HOLD") return;
        if (!stockAgg[d.stock]) stockAgg[d.stock] = { symbol: d.stock, sector: STOCKS_DATA[d.stock]?.sector || "", name: STOCKS_DATA[d.stock]?.name || d.stock, buyShares: 0, sellShares: 0, buyValue: 0, sellValue: 0, buyCount: 0, sellCount: 0, topBuyers: {}, topSellers: {} };
        const s = stockAgg[d.stock];
        if (d.action === "BUY") {
          s.buyShares += d.shares; s.buyValue += d.shares * d.price; s.buyCount++;
          s.topBuyers[d.agentName] = (s.topBuyers[d.agentName] || 0) + d.shares;
        } else {
          s.sellShares += d.shares; s.sellValue += d.shares * d.price; s.sellCount++;
          s.topSellers[d.agentName] = (s.topSellers[d.agentName] || 0) + d.shares;
        }
      });
    });
    const all = Object.values(stockAgg);
    const topBought = [...all].sort((a, b) => b.buyValue - a.buyValue).slice(0, 10);
    const topSold = [...all].sort((a, b) => b.sellValue - a.sellValue).slice(0, 10);
    // Net movers
    const netMovers = [...all].map(s => ({ ...s, net: s.buyValue - s.sellValue })).sort((a, b) => Math.abs(b.net) - Math.abs(a.net)).slice(0, 10);
    // Total market stats
    const totalBuyVal = all.reduce((s, x) => s + x.buyValue, 0);
    const totalSellVal = all.reduce((s, x) => s + x.sellValue, 0);
    const totalBuyShares = all.reduce((s, x) => s + x.buyShares, 0);
    const totalSellShares = all.reduce((s, x) => s + x.sellShares, 0);
    const uniqueStocksTraded = all.length;
    // Sector breakdown
    const sectorAgg = {};
    all.forEach(s => {
      if (!sectorAgg[s.sector]) sectorAgg[s.sector] = { buyVal: 0, sellVal: 0 };
      sectorAgg[s.sector].buyVal += s.buyValue;
      sectorAgg[s.sector].sellVal += s.sellValue;
    });
    return { topBought, topSold, netMovers, totalBuyVal, totalSellVal, totalBuyShares, totalSellShares, uniqueStocksTraded, sectorAgg };
  })();

  return (
    <div style={S.page}>
      {/* ━━ TOP BAR ━━ */}
      <div style={S.topBar}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
            {Object.values(ARCHETYPE_CONFIG).slice(0, 4).map((c, i) => (
              <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: c.color, opacity: 0.7 }} />
            ))}
          </div>
          <span style={{ ...S.heading, fontSize: 14, color: "#0f172a", flexShrink: 0 }}>MiroFish</span>
          <div style={{ height: 16, width: 1, background: "#e2e8f0", flexShrink: 0 }} />
          <span style={{ ...S.heading, fontSize: 12, color: "#64748b", fontStyle: "italic", fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {headline}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: "#94a3b8", fontFamily: "Georgia, serif" }}>Step {step}</span>
          <div style={{ display: "flex", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
            {[1, 2, 5].map(s => (
              <button key={s} onClick={() => setSpeed(s)}
                style={{ padding: "4px 10px", fontSize: 10, fontWeight: 600, border: "none", cursor: "pointer", background: speed === s ? "#0f172a" : "#fff", color: speed === s ? "#fff" : "#64748b" }}>
                {s}x
              </button>
            ))}
          </div>
          <button onClick={() => setRunning(!running)}
            style={{ ...S.btnSmall, background: running ? "#fef2f2" : "#dcfce7", color: running ? "#991b1b" : "#166534", borderColor: running ? "#fecaca" : "#bbf7d0" }}>
            {running ? "Pause" : "Resume"}
          </button>
          <button onClick={() => { setRunning(false); setPhase("input"); setHeadline(""); setSelectedAgent(null); didAutoFetch.current = false; setLiveHeadlines([]); setHeadlinesError(null); }}
            style={{ ...S.btnSmall, background: "#f8fafc", color: "#64748b", borderColor: "#e2e8f0" }}>
            Reset
          </button>
        </div>
      </div>

      {/* ━━ STATS ROW ━━ */}
      <div style={{ display: "flex", gap: 1, background: "#e2e8f0", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" }}>
        {[
          { label: "Agents", value: agents.length, color: "#0f172a" },
          { label: "Market Sentiment", value: mktSentiment.toFixed(3), color: mktSentiment > 0.1 ? "#166534" : mktSentiment < -0.1 ? "#991b1b" : "#475569" },
          { label: "Total Buys", value: totalOrders.buys.toLocaleString(), color: "#166534" },
          { label: "Total Sells", value: totalOrders.sells.toLocaleString(), color: "#991b1b" },
          { label: "Cascade", value: cascade.active ? `${(cascade.pct * 100).toFixed(0)}% ${cascade.direction}` : "None", color: cascade.active ? "#dc2626" : "#64748b" },
        ].map((stat, i) => (
          <div key={i} style={{ flex: 1, background: "#fff", padding: "8px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>{stat.label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: stat.color, fontFamily: "system-ui" }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* ━━ CASCADE ALERT ━━ */}
      {cascade.active && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "8px 16px",
          background: cascade.direction === "bullish" ? "#f0fdf4" : "#fef2f2",
          borderBottom: `1px solid ${cascade.direction === "bullish" ? "#bbf7d0" : "#fecaca"}`,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: cascade.direction === "bullish" ? "#166534" : "#dc2626", boxShadow: `0 0 10px ${cascade.direction === "bullish" ? "#16653480" : "#dc262680"}`, animation: "pulse 1.2s infinite" }} />
          <span style={{ ...S.heading, fontSize: 12, fontWeight: 600, color: cascade.direction === "bullish" ? "#166534" : "#991b1b" }}>
            {cascade.direction === "bullish" ? "Bullish" : "Bearish"} Herding Cascade
          </span>
          <span style={{ fontSize: 11, color: "#64748b" }}>
            {(cascade.pct * 100).toFixed(0)}% of agents aligned
          </span>
        </div>
      )}

      {/* ━━ PAUSE SUMMARY ━━ */}
      {!running && step > 0 && pauseSummary && (
        <div style={{ animation: "fadeIn 0.3s ease", background: "#fff", borderBottom: "1px solid #e2e8f0" }}>
          {/* Summary header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: "1px solid #f1f5f9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", boxShadow: "0 0 8px rgba(245,158,11,0.4)" }} />
              <span style={{ ...S.heading, fontSize: 14, color: "#0f172a", fontWeight: 600 }}>Simulation Paused</span>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>Step {step} | {pauseSummary.uniqueStocksTraded} stocks traded</span>
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Buy Volume</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#166534" }}>{fmt$(pauseSummary.totalBuyVal)}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Sell Volume</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#991b1b" }}>{fmt$(pauseSummary.totalSellVal)}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Net Flow</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: pauseSummary.totalBuyVal > pauseSummary.totalSellVal ? "#166534" : "#991b1b" }}>
                  {pauseSummary.totalBuyVal > pauseSummary.totalSellVal ? "+" : ""}{fmt$(pauseSummary.totalBuyVal - pauseSummary.totalSellVal)}
                </div>
              </div>
            </div>
          </div>

          {/* Two-column: Top Bought / Top Sold */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }} className="two-col">
            {/* TOP 10 BOUGHT */}
            <div style={{ padding: "12px 16px", borderRight: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 1L9 6H1z" fill="#166534"/></svg>
                </div>
                <span style={{ ...S.heading, fontSize: 12, color: "#166534", fontWeight: 600 }}>Top 10 Stocks Bought</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {pauseSummary.topBought.map((s, i) => {
                  const topBuyer = Object.entries(s.topBuyers).sort((a, b) => b[1] - a[1])[0];
                  const maxVal = pauseSummary.topBought[0]?.buyValue || 1;
                  const mkt = STOCKS_DATA[s.symbol]?.market || "US";
                  return (
                    <div key={s.symbol} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: i % 2 === 0 ? "#f8fdf9" : "#fff", borderRadius: 6 }}>
                      <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, width: 16, flexShrink: 0 }}>{i + 1}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{s.symbol}</span>
                          <span style={{ fontSize: 7, fontWeight: 700, padding: "1px 3px", borderRadius: 2, background: mkt === "TSX" ? "#fef3c7" : "#dbeafe", color: mkt === "TSX" ? "#92400e" : "#1e40af" }}>{mkt}</span>
                          {stocks[s.symbol] && <span style={{ fontSize: 9, fontWeight: 600, color: stocks[s.symbol].changePct >= 0 ? "#166534" : "#991b1b" }}>{stocks[s.symbol].changePct >= 0 ? "+" : ""}{stocks[s.symbol].changePct.toFixed(1)}%</span>}
                        </div>
                        <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 1 }}>{s.sector}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#166534" }}>{fmt$(s.buyValue)}</div>
                        <div style={{ fontSize: 9, color: "#64748b" }}>{s.buyShares.toLocaleString()} shares | {s.buyCount} orders</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* TOP 10 SOLD */}
            <div style={{ padding: "12px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 9L1 4h8z" fill="#991b1b"/></svg>
                </div>
                <span style={{ ...S.heading, fontSize: 12, color: "#991b1b", fontWeight: 600 }}>Top 10 Stocks Sold</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {pauseSummary.topSold.map((s, i) => {
                  const topSeller = Object.entries(s.topSellers).sort((a, b) => b[1] - a[1])[0];
                  const maxVal = pauseSummary.topSold[0]?.sellValue || 1;
                  const mkt = STOCKS_DATA[s.symbol]?.market || "US";
                  return (
                    <div key={s.symbol} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: i % 2 === 0 ? "#fef8f8" : "#fff", borderRadius: 6 }}>
                      <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, width: 16, flexShrink: 0 }}>{i + 1}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{s.symbol}</span>
                          <span style={{ fontSize: 7, fontWeight: 700, padding: "1px 3px", borderRadius: 2, background: mkt === "TSX" ? "#fef3c7" : "#dbeafe", color: mkt === "TSX" ? "#92400e" : "#1e40af" }}>{mkt}</span>
                          {stocks[s.symbol] && <span style={{ fontSize: 9, fontWeight: 600, color: stocks[s.symbol].changePct >= 0 ? "#166534" : "#991b1b" }}>{stocks[s.symbol].changePct >= 0 ? "+" : ""}{stocks[s.symbol].changePct.toFixed(1)}%</span>}
                        </div>
                        <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 1 }}>{s.sector}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#991b1b" }}>{fmt$(s.sellValue)}</div>
                        <div style={{ fontSize: 9, color: "#64748b" }}>{s.sellShares.toLocaleString()} shares | {s.sellCount} orders</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sector Flow */}
          <div style={{ padding: "10px 20px 14px", borderTop: "1px solid #f1f5f9" }}>
            <div style={{ ...S.label, marginBottom: 8, fontSize: 10 }}>Sector Capital Flow</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {Object.entries(pauseSummary.sectorAgg).sort((a, b) => (b[1].buyVal + b[1].sellVal) - (a[1].buyVal + a[1].sellVal)).map(([sector, data]) => {
                const net = data.buyVal - data.sellVal;
                const total = data.buyVal + data.sellVal;
                const buyPct = total > 0 ? (data.buyVal / total) * 100 : 50;
                return (
                  <div key={sector} style={{ background: "#f8fafc", borderRadius: 8, padding: "8px 14px", minWidth: 130, flex: "1 1 0" }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#475569", marginBottom: 4 }}>{sector}</div>
                    <div style={{ height: 6, borderRadius: 3, background: "#e2e8f0", overflow: "hidden", display: "flex", marginBottom: 4 }}>
                      <div style={{ width: `${buyPct}%`, background: "#166534", opacity: 0.6, transition: "width 0.3s" }} />
                      <div style={{ width: `${100 - buyPct}%`, background: "#991b1b", opacity: 0.6, transition: "width 0.3s" }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9 }}>
                      <span style={{ color: "#166534", fontWeight: 600 }}>{fmt$(data.buyVal)}</span>
                      <span style={{ color: net >= 0 ? "#166534" : "#991b1b", fontWeight: 700 }}>
                        Net: {net >= 0 ? "+" : ""}{fmt$(net)}
                      </span>
                      <span style={{ color: "#991b1b", fontWeight: 600 }}>{fmt$(data.sellVal)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Investor Themes */}
          {(() => {
            const themes = extractThemes(agents);
            if (themes.length === 0) return null;
            return (
              <div style={{ padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
                <div style={{ ...S.label, marginBottom: 10, fontSize: 10 }}>Dominant Investor Themes</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} className="two-col">
                  {themes.slice(0, 6).map(theme => (
                    <div key={theme.id} style={{ background: theme.bg, borderRadius: 8, padding: "12px 14px", border: `1px solid ${theme.color}20` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: theme.color }}>{theme.label}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: theme.color, background: "#fff", padding: "1px 8px", borderRadius: 10 }}>{theme.count} mentions</span>
                      </div>
                      {theme.quotes.map((q, qi) => (
                        <div key={qi} style={{ marginBottom: qi < theme.quotes.length - 1 ? 6 : 0, paddingLeft: 8, borderLeft: `2px solid ${theme.color}40` }}>
                          <div style={{ fontSize: 10, color: "#0f172a", fontStyle: "italic", lineHeight: 1.4 }}>"{q.text}"</div>
                          <div style={{ fontSize: 9, color: "#64748b", marginTop: 2 }}>
                            <span style={{ fontWeight: 600 }}>{q.name}</span>
                            <span style={{ margin: "0 4px" }}> </span>
                            <span style={{ fontSize: 8, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: ARCHETYPE_CONFIG[q.archetype]?.light, color: ARCHETYPE_CONFIG[q.archetype]?.color }}>{ARCHETYPE_CONFIG[q.archetype]?.label}</span>
                            <span style={{ margin: "0 4px" }}> </span>
                            <span style={{ fontWeight: 600, color: q.action === "BUY" ? "#166534" : "#991b1b" }}>{q.action} {q.stock}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── Generate Report ── */}
          <div style={{ padding: "14px 20px", borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ ...S.heading, fontSize: 13, fontWeight: 600, color: "#0f172a" }}>ReportAgent Analysis</div>
              <div style={{ fontSize: 10, color: "#94a3b8" }}>AI interviews spotlight agents, analyzes biases, and generates a structured report</div>
            </div>
            <button
              onClick={generateReport}
              disabled={reportLoading}
              style={{
                padding: "8px 20px", fontSize: 12, fontFamily: "Georgia, serif", fontWeight: 600,
                background: reportLoading ? "#f1f5f9" : "#0f172a", color: reportLoading ? "#94a3b8" : "#fff",
                border: "none", borderRadius: 8, cursor: reportLoading ? "default" : "pointer",
              }}
            >
              {reportLoading ? "Analyzing..." : report ? "Regenerate Report" : "Generate Report"}
            </button>
          </div>

          {/* Report loading */}
          {reportLoading && (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#1e40af", margin: "0 auto 10px", animation: "pulse 1s infinite" }} />
              <div style={{ fontSize: 12, color: "#64748b", fontStyle: "italic" }}>ReportAgent is interviewing agents and analyzing behavioral patterns...</div>
              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>Scanning {agents.length} agents across {Object.keys(stocks).length} stocks</div>
            </div>
          )}

          {/* Report content */}
          {report && !reportLoading && (
            <div style={{ padding: "16px 20px", borderTop: "1px solid #f1f5f9" }}>
              <div style={{ background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", padding: "20px 24px", maxHeight: 500, overflowY: "auto" }}>
                {report.split("\n\n").map((para, pi) => {
                  const isHeader = para === para.toUpperCase() || /^[\d]+\.\s*[A-Z]/.test(para) || /^[A-Z][A-Z\s&:]+$/.test(para.split("\n")[0]);
                  if (isHeader || /^[A-Z\d].*:?\s*$/.test(para.split("\n")[0]) && para.split("\n")[0].length < 60) {
                    const lines = para.split("\n");
                    return (
                      <div key={pi} style={{ marginBottom: 14 }}>
                        <div style={{ ...S.heading, fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 4, borderBottom: "1px solid #e2e8f0", paddingBottom: 4 }}>
                          {lines[0]}
                        </div>
                        {lines.slice(1).map((l, li) => (
                          <p key={li} style={{ fontSize: 12, color: "#334155", lineHeight: 1.6, margin: "4px 0" }}>{l}</p>
                        ))}
                      </div>
                    );
                  }
                  return <p key={pi} style={{ fontSize: 12, color: "#334155", lineHeight: 1.6, margin: "0 0 10px" }}>{para}</p>;
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ━━ MAIN LAYOUT ━━ */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 12, padding: "12px 16px" }} className="main-grid">
        {/* LEFT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* SWARM */}
          <div style={{ ...S.card, padding: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "8px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <span style={S.label}>Agent Swarm</span>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {Object.entries(ARCHETYPE_CONFIG).map(([k, c]) => (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: c.color }} />
                    <span style={{ fontSize: 9, color: "#64748b" }}>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%" }}>
              <canvas ref={canvasRef} onClick={onCanvasClick} onMouseMove={onCanvasMove}
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", cursor: hoveredAgent ? "pointer" : "crosshair", display: "block" }} />
            </div>
          </div>

          {/* BOTTOM PANELS */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="two-col">
            {/* Sentiment */}
            <div style={{ ...S.card, padding: 16 }}>
              <div style={{ ...S.label, marginBottom: 12 }}>Sentiment Gauge</div>
              <div style={{ position: "relative", height: 24, background: "linear-gradient(90deg, #fee2e2, #fef3c7, #dcfce7)", borderRadius: 12, marginBottom: 6 }}>
                <div style={{
                  position: "absolute", left: `calc(${sentPct}% - 10px)`, top: -3,
                  width: 20, height: 30, borderRadius: 10, background: "#0f172a", border: "3px solid #fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)", transition: "left 0.2s",
                }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#94a3b8" }}>
                <span>Fear</span>
                <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 12 }}>{mktSentiment > 0.1 ? "Bullish" : mktSentiment < -0.1 ? "Bearish" : "Neutral"}</span>
                <span>Greed</span>
              </div>
              {sentHistory.length > 3 && (
                <svg viewBox={`0 0 ${sentHistory.length} 36`} style={{ width: "100%", height: 32, marginTop: 8 }} preserveAspectRatio="none">
                  <line x1="0" y1="18" x2={sentHistory.length} y2="18" stroke="#e2e8f0" strokeWidth="0.5" />
                  <polyline fill="none" stroke="#1e40af" strokeWidth="1.2" strokeLinejoin="round"
                    points={sentHistory.map((s, i) => `${i},${18 - s * 16}`).join(" ")} />
                </svg>
              )}
            </div>

            {/* Archetype Breakdown */}
            <div style={{ ...S.card, padding: 16 }}>
              <div style={{ ...S.label, marginBottom: 10 }}>Archetype Breakdown</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {Object.entries(ARCHETYPE_CONFIG).map(([k, cfg]) => {
                  const st = archetypeStats[k];
                  const total = st.buy + st.sell + st.hold || 1;
                  return (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: cfg.light, border: `1.5px solid ${cfg.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, color: cfg.color, flexShrink: 0 }}>
                        {cfg.icon}
                      </div>
                      <span style={{ fontSize: 10, color: "#475569", width: 60, flexShrink: 0, fontWeight: 600 }}>{cfg.label}</span>
                      <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#f1f5f9", overflow: "hidden", display: "flex" }}>
                        <div style={{ width: `${(st.buy / total) * 100}%`, background: "#166534", transition: "width 0.3s" }} />
                        <div style={{ width: `${(st.sell / total) * 100}%`, background: "#991b1b", transition: "width 0.3s" }} />
                      </div>
                      <span style={{ fontSize: 9, color: "#94a3b8", width: 48, textAlign: "right", flexShrink: 0 }}>
                        <span style={{ color: "#166534" }}>{st.buy}</span>/<span style={{ color: "#991b1b" }}>{st.sell}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }} className="sidebar">

          {/* Agent Detail */}
          {selectedAgent && (
            <div style={{ ...S.card, padding: 16, borderColor: ARCHETYPE_CONFIG[selectedAgent.archetype]?.color || "#e2e8f0", borderWidth: 2, overflow: "auto", maxHeight: "70vh" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <div>
                  <div style={{ ...S.heading, fontSize: 15, color: "#0f172a", fontWeight: 600 }}>{selectedAgent.name}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
                    <span style={{ ...S.badge, background: ARCHETYPE_CONFIG[selectedAgent.archetype]?.light, color: ARCHETYPE_CONFIG[selectedAgent.archetype]?.color }}>
                      {ARCHETYPE_CONFIG[selectedAgent.archetype]?.label}
                    </span>
                    <span style={{ ...S.badge, background: "#f1f5f9", color: "#475569" }}>{selectedAgent.riskTolerance}</span>
                  </div>
                </div>
                <button onClick={() => setSelectedAgent(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, padding: 4 }}>x</button>
              </div>

              <p style={{ fontSize: 11, color: "#64748b", margin: "0 0 10px", lineHeight: 1.5, fontStyle: "italic" }}>
                {ARCHETYPE_CONFIG[selectedAgent.archetype]?.desc}
              </p>

              {/* Bias bars */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ ...S.label, marginBottom: 6, fontSize: 10 }}>Cognitive Bias Profile</div>
                {selectedAgent.biases && Object.entries(selectedAgent.biases).map(([b, v]) => (
                  <div key={b} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 9, color: "#64748b", width: 70, textTransform: "capitalize" }}>{b.replace(/([A-Z])/g, ' $1')}</span>
                    <div style={{ flex: 1, height: 5, borderRadius: 3, background: "#f1f5f9", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.abs(v) * 100}%`, background: v < 0 ? "#0e7490" : ARCHETYPE_CONFIG[selectedAgent.archetype]?.color, borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 9, color: "#94a3b8", width: 28, textAlign: "right" }}>{v.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Holdings */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ ...S.label, marginBottom: 4, fontSize: 10 }}>Holdings</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {selectedAgent.holdings?.map(s => (
                    <span key={s} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "#f1f5f9", color: "#475569", fontWeight: 600 }}>{s}</span>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#64748b", marginBottom: 10 }}>
                <span>Portfolio: <strong style={{ color: "#0f172a" }}>{fmt$(selectedAgent.portfolioValue || 0)}</strong></span>
                <span>Influence: <strong style={{ color: "#0f172a" }}>{((selectedAgent.influence || 0) * 100).toFixed(0)}%</strong></span>
              </div>

              {/* Quick Chat Buttons - prominent position */}
              <div style={{ marginBottom: 12, padding: "10px 12px", background: ARCHETYPE_CONFIG[selectedAgent.archetype]?.light || "#f1f5f9", borderRadius: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: ARCHETYPE_CONFIG[selectedAgent.archetype]?.color, marginBottom: 6 }}>
                  Ask {selectedAgent.name}:
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {["Why did you sell?", "Why did you buy?", "What's your outlook?", "Are you worried?", "What bias drove you?", "What would change your mind?"].map(q => (
                    <button key={q} onClick={() => { setChatInput(q); chatWithAgent(q); }}
                      style={{ fontSize: 9, padding: "4px 10px", borderRadius: 5, border: `1px solid ${ARCHETYPE_CONFIG[selectedAgent.archetype]?.color}30`, background: "#fff", color: ARCHETYPE_CONFIG[selectedAgent.archetype]?.color, cursor: "pointer", fontWeight: 600, transition: "all 0.15s" }}
                      onMouseEnter={e => { e.currentTarget.style.background = ARCHETYPE_CONFIG[selectedAgent.archetype]?.color; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = ARCHETYPE_CONFIG[selectedAgent.archetype]?.color; }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
                {/* Inline chat response */}
                {chatMessages.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {chatMessages.slice(-2).map((m, i) => (
                      <div key={i} style={{
                        padding: "6px 10px", borderRadius: 8, fontSize: 11, lineHeight: 1.45, marginBottom: 4,
                        background: m.role === "user" ? "#dbeafe" : "#fff",
                        color: "#0f172a",
                        marginLeft: m.role === "user" ? 16 : 0,
                        marginRight: m.role === "agent" ? 16 : 0,
                        border: m.role === "agent" ? `1px solid ${ARCHETYPE_CONFIG[selectedAgent.archetype]?.color}20` : "none",
                      }}>
                        <div style={{ fontSize: 9, fontWeight: 600, color: m.role === "user" ? "#1e40af" : ARCHETYPE_CONFIG[selectedAgent.archetype]?.color, marginBottom: 2 }}>
                          {m.role === "user" ? "You" : selectedAgent.name}
                        </div>
                        {m.text}
                      </div>
                    ))}
                    {chatLoading && (
                      <div style={{ fontSize: 10, color: "#94a3b8", fontStyle: "italic", padding: "4px 10px" }}>
                        {selectedAgent.name} is thinking...
                      </div>
                    )}
                  </div>
                )}
                {/* Custom question input */}
                <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                  <input
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !chatLoading && chatInput.trim() && chatWithAgent(chatInput)}
                    placeholder="Or type your own question..."
                    style={{ flex: 1, padding: "6px 8px", fontSize: 10, border: `1px solid ${ARCHETYPE_CONFIG[selectedAgent.archetype]?.color}30`, borderRadius: 5, outline: "none", background: "#fff" }}
                    onFocus={e => e.target.style.borderColor = ARCHETYPE_CONFIG[selectedAgent.archetype]?.color}
                    onBlur={e => e.target.style.borderColor = ARCHETYPE_CONFIG[selectedAgent.archetype]?.color + "30"}
                  />
                  <button
                    onClick={() => !chatLoading && chatInput.trim() && chatWithAgent(chatInput)}
                    disabled={chatLoading || !chatInput.trim()}
                    style={{
                      padding: "6px 10px", fontSize: 9, fontWeight: 700, border: "none", borderRadius: 5, cursor: chatLoading ? "default" : "pointer",
                      background: ARCHETYPE_CONFIG[selectedAgent.archetype]?.color || "#1e40af", color: "#fff",
                      opacity: chatLoading || !chatInput.trim() ? 0.4 : 1,
                    }}
                  >
                    Ask
                  </button>
                </div>
              </div>

              {/* Decisions */}
              {selectedAgent.decisionHistory?.length > 0 && (
                <div>
                  <div style={{ ...S.label, marginBottom: 4, fontSize: 10 }}>Recent Trades</div>
                  {selectedAgent.decisionHistory.slice(-6).reverse().map((d, i) => (
                    <div key={i} style={{ padding: "5px 0", borderBottom: "1px solid #f8fafc" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: d.action === "BUY" ? "#dcfce7" : "#fee2e2", color: d.action === "BUY" ? "#166534" : "#991b1b" }}>{d.action}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: "#0f172a" }}>{d.stock}</span>
                        <span style={{ fontSize: 9, color: "#94a3b8" }}>{d.shares} @ ${d.price?.toFixed(1)}</span>
                      </div>
                      {d.reasoning && <div style={{ fontSize: 9, color: "#64748b", fontStyle: "italic", marginTop: 2, lineHeight: 1.35 }}>{d.reasoning}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab selector */}
          <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0" }}>
            {[{ key: "flow", label: "Order Flow" }, { key: "stocks", label: "Stocks" }].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{ flex: 1, padding: "8px 0", fontSize: 11, fontFamily: "Georgia, serif", fontWeight: 600, color: tab === t.key ? "#0f172a" : "#94a3b8", background: "none", border: "none", borderBottom: tab === t.key ? "2px solid #0f172a" : "2px solid transparent", cursor: "pointer" }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ ...S.card, padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: 480 }}>
            {tab === "flow" && (
              <div style={{ flex: 1, overflow: "auto", padding: "6px 0" }}>
                {orderFlow.length === 0 && (
                  <div style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>Agents analyzing headline...</div>
                )}
                {orderFlow.map((o, i) => {
                  const cfg = ARCHETYPE_CONFIG[o.archetype];
                  return (
                    <div key={o.id}
                      onClick={() => { const ag = dataRef.current.agents.find(a => a.id === o.agentId); if (ag) setSelectedAgent({ ...ag }); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", cursor: "pointer", opacity: 1 - i * 0.015, borderBottom: "1px solid #fafafa" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: cfg?.light, border: `1.5px solid ${cfg?.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: cfg?.color, flexShrink: 0 }}>
                        {cfg?.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }}>{o.agentName}</span>
                          <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 4px", borderRadius: 3, background: o.action === "BUY" ? "#dcfce7" : "#fee2e2", color: o.action === "BUY" ? "#166534" : "#991b1b" }}>{o.action}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>{o.shares} {o.stock} @ ${o.price?.toFixed(1)}</div>
                        {o.reasoning && <div style={{ fontSize: 9, color: "#64748b", fontStyle: "italic", marginTop: 2, lineHeight: 1.3 }}>{o.reasoning}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {tab === "stocks" && (
              <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
                {topStocks.length === 0 && (
                  <div style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>Waiting for trades...</div>
                )}
                {topStocks.map(([sym, s]) => {
                  const buyPct = s.buyPressure / (s.buyPressure + s.sellPressure + 0.001) * 100;
                  return (
                    <div key={sym} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid #f8fafc" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{sym}</span>
                          <span style={{ fontSize: 8, fontWeight: 700, padding: "1px 4px", borderRadius: 3, marginLeft: 4, background: s.market === "TSX" ? "#fef3c7" : "#dbeafe", color: s.market === "TSX" ? "#92400e" : "#1e40af" }}>{s.market || "US"}</span>
                          <span style={{ fontSize: 9, color: "#94a3b8", marginLeft: 6 }}>{STOCKS_DATA[sym]?.sector}</span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>${s.price.toFixed(2)}</div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: s.changePct >= 0 ? "#166534" : "#991b1b" }}>
                            {s.changePct >= 0 ? "+" : ""}{s.changePct.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                      <div style={{ height: 8, borderRadius: 4, background: "#f1f5f9", overflow: "hidden", display: "flex" }}>
                        <div style={{ width: `${buyPct}%`, background: "#166534", opacity: 0.6 }} />
                        <div style={{ width: `${100 - buyPct}%`, background: "#991b1b", opacity: 0.6 }} />
                      </div>
                      {/* Mini sparkline */}
                      {s.history.length > 3 && (
                        <svg viewBox={`0 0 ${s.history.length} 20`} style={{ width: "100%", height: 16, marginTop: 4 }} preserveAspectRatio="none">
                          <polyline fill="none" stroke={s.changePct >= 0 ? "#166534" : "#991b1b"} strokeWidth="1" strokeLinejoin="round"
                            points={s.history.map((p, i) => {
                              const min = Math.min(...s.history);
                              const max = Math.max(...s.history);
                              const range = max - min || 1;
                              return `${i},${18 - ((p - min) / range) * 16}`;
                            }).join(" ")} />
                        </svg>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#94a3b8", marginTop: 2 }}>
                        <span>Vol: {s.volume.toLocaleString()}</span>
                        <span>Buy: {(buyPct).toFixed(0)}% | Sell: {(100 - buyPct).toFixed(0)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }

        /* Responsive: medium screens */
        @media (max-width: 1024px) {
          .main-grid { grid-template-columns: 1fr 280px !important; gap: 10px !important; }
        }
        /* Responsive: tablet / small */
        @media (max-width: 900px) {
          .main-grid { grid-template-columns: 1fr !important; }
          .sidebar { max-height: 600px; overflow-y: auto; }
        }
        /* Responsive: two-column grids stack */
        @media (max-width: 700px) {
          .two-col { grid-template-columns: 1fr !important; }
          .main-grid { padding: 8px !important; gap: 8px !important; }
        }
      `}</style>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STYLES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const S = {
  page: { fontFamily: "system-ui, -apple-system, sans-serif", background: "#f8fafc", minHeight: "100vh" },
  heading: { fontFamily: "Georgia, serif" },
  card: { background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", boxShadow: "0 1px 2px rgba(0,0,0,0.03)", overflow: "hidden" },
  label: { fontFamily: "Georgia, serif", fontSize: 11, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 },
  badge: { fontSize: 9, fontWeight: 600, padding: "2px 7px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { flex: 1, padding: "13px 16px", fontSize: 14, fontFamily: "system-ui", border: "1px solid #cbd5e1", borderRadius: 8, outline: "none", color: "#0f172a", background: "#f8fafc", transition: "border-color 0.2s" },
  btnPrimary: { padding: "13px 26px", fontSize: 13, fontFamily: "Georgia, serif", fontWeight: 600, background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, letterSpacing: "0.02em", cursor: "pointer", transition: "opacity 0.2s", whiteSpace: "nowrap" },
  btnSmall: { padding: "5px 14px", fontSize: 11, fontFamily: "Georgia, serif", fontWeight: 600, border: "1px solid", borderRadius: 6, cursor: "pointer" },
  headlineCard: { textAlign: "left", padding: "14px 16px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", transition: "all 0.2s" },
  topBar: { background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, position: "sticky", top: 0, zIndex: 10 },
};
